/**
 * PackManagerPage — Runtime Pack 管理（平台默认 + 租户覆盖）
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import AdminTenantScopeBar from '@/components/AdminTenantScopeBar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EntityDiscussionPanel } from '@/components/entity/EntityDiscussionPanel';
import { useChatContextStore } from '@/stores/chat-context-store';
import { AlertTriangle, Loader2, Package, RefreshCw, Settings, ShieldAlert, Wand2 } from 'lucide-react';
import {
  BUSINESS_PACKAGE_ARCHETYPE_PRESETS,
  type BusinessPackageArchetypePreset,
} from './business-package-archetypes';
import {
  BUSINESS_PACKAGE_SHOWCASES,
  getBusinessPackageShowcase,
} from './business-package-showcases';

type PackItem = {
  id: string;
  version: string;
  name: string;
  description?: string;
  status: string;
  manifest: Record<string, unknown>;
  enabled: boolean;
  config: Record<string, unknown>;
};

type BusinessPackageRelease = {
  id: string;
  packageId: string;
  version: string;
  kind: string;
  ownerScope: string;
  ownerRef: string;
  releaseChannel: string;
  status: string;
  manifest: Record<string, unknown>;
  installCount?: number;
  activeInstallCount?: number;
  exchangeListingId?: string | null;
  exchangeListingStatus?: string | null;
};

type ComposerDraft = {
  id: string;
  draftType: string;
  name: string;
  slug: string;
  status: string;
  manifest?: Record<string, unknown>;
  interviewState?: Record<string, unknown>;
  updatedAt?: string;
};

type DraftDetail = {
  draft: ComposerDraft;
  latestPreflight?: {
    decision?: string;
    executedAt?: string;
    result?: {
      recommendedNextAction?: string;
      issues?: Array<Record<string, unknown>>;
    };
  } | null;
  latestSimulation?: {
    mode?: string;
    executedAt?: string;
    result?: {
      summary?: string;
      signals?: Array<Record<string, unknown>>;
      mockScenarioRefs?: string[];
      approvalCheckpoints?: string[];
      riskFindings?: Array<Record<string, unknown>>;
      operatorNotes?: string[];
    };
  } | null;
  derivedRelease?: BusinessPackageRelease | null;
  recentInstalls?: Array<{
    id: string;
    environment: string;
    installMode: string;
    status: string;
    runtimeStatus: string;
    installedAt?: string;
  }>;
};

type ReleaseDetail = {
  release: BusinessPackageRelease;
  installSummary?: {
    installCount: number;
    activeInstallCount: number;
  };
  runtimeBindingSummary?: Record<string, { total: number; active: number }>;
  recentInstalls?: Array<{
    id: string;
    tenantId: string;
    environment: string;
    installMode: string;
    status: string;
    runtimeStatus: string;
    installedAt?: string;
  }>;
  exchangeListings?: Array<{
    id: string;
    scope: string;
    status: string;
    discoverability?: string;
    approvalRequired?: boolean;
    updatedAt?: string;
  }>;
  lineage?: {
    parents?: Array<{
      id: string;
      lineageType: string;
      divergenceReason?: string;
      releaseId?: string;
      packageId?: string;
      version?: string;
    }>;
    children?: Array<{
      id: string;
      lineageType: string;
      divergenceReason?: string;
      releaseId?: string;
      packageId?: string;
      version?: string;
    }>;
  };
};

type PackageInstall = {
  id: string;
  tenantId: string;
  packageReleaseId: string;
  effectiveManifest?: Record<string, unknown>;
  environment: string;
  installMode: string;
  status: string;
  runtimeStatus: string;
  rollbackPointRef?: string;
  lastValidatedAt?: string;
};

type InstallDetail = {
  install: PackageInstall;
  release?: BusinessPackageRelease | null;
  runtimeBindings?: Array<{
    id: string;
    bindingType: string;
    bindingRef: string;
    bindingState: string;
    createdAt?: string;
  }>;
  latestSimulation?: Record<string, unknown> | null;
  recentEvents?: Array<{
    id: string;
    type: string;
    actorId?: string;
    createdAt?: string;
  }>;
  recentRollbacks?: Array<{
    id: string;
    rollbackPointRef?: string;
    actorId?: string;
    createdAt?: string;
  }>;
};

type ExchangeListing = {
  listingId: string;
  scope: string;
  status?: string;
  approvalRequired?: boolean;
  releaseId: string;
  packageId: string;
  version: string;
  kind: string;
  name?: string;
  slug?: string;
};

type WorkspaceGraphNode = {
  id: string;
  label: string;
  kind: string;
};

type WorkspaceGraphEdge = {
  from: string;
  to: string;
  label: string;
};

type AdvisorChecklistCategory = 'interview' | 'data' | 'policy' | 'sharing' | 'runtime';

type AdvisorChecklistGroups = Record<AdvisorChecklistCategory, string[]>;

type AdvisorDiagnosis = {
  readiness: 'needs_input' | 'guided_ready' | 'blueprint_ready';
  installGate: 'blocked' | 'review_required' | 'ready';
  summary: string;
  recommendedOverlay: string;
  installShape: string;
  recommendedSurfaces: string[];
  recommendedCapabilities: string[];
  recommendedEntities: string[];
  humanAssistMoments: string[];
  riskFlags: string[];
  complianceSignals: string[];
  missingItems: string[];
  blockingItems: string[];
  reviewItems: string[];
  blockingGroups: AdvisorChecklistGroups;
  reviewGroups: AdvisorChecklistGroups;
  missingGroups: AdvisorChecklistGroups;
  nextActions: string[];
};

type AdvisorWorkspaceContext = {
  packageId?: string;
  intentSummary?: string;
  riskClass?: string;
  installMode?: string;
  trustScopeLabels: string[];
  capabilityLabels: string[];
  workspaceLabels: string[];
  agentLabels: string[];
  entityLabels: string[];
  latestPreflightDecision?: string;
  latestPreflightNextAction?: string;
  latestSimulationMode?: string;
  latestSimulationSummary?: string;
  latestPreflightIssues?: Array<Record<string, unknown>>;
  latestSimulationSignals?: Array<Record<string, unknown>>;
  latestSimulationScenarios?: string[];
  latestSimulationRiskFindings?: Array<Record<string, unknown>>;
};

function normalizeAdvisorText(value: string): string {
  return value.trim().toLowerCase();
}

function createEmptyAdvisorGroups(): AdvisorChecklistGroups {
  return {
    interview: [],
    data: [],
    policy: [],
    sharing: [],
    runtime: [],
  };
}

function appendAdvisorGroup(groups: AdvisorChecklistGroups, category: AdvisorChecklistCategory, label: string) {
  if (!groups[category].includes(label)) groups[category].push(label);
}

const ADVISOR_CATEGORY_LABELS: Record<AdvisorChecklistCategory, string> = {
  interview: 'Interview',
  data: 'Data',
  policy: 'Policy',
  sharing: 'Sharing',
  runtime: 'Runtime',
};

function getAdvisorAnswerText(answers: Record<string, string>, questionId: string): string {
  return normalizeAdvisorText(String(answers[questionId] ?? ''));
}

function isAdvisorQuestionActive(
  question: BusinessPackageArchetypePreset['advisorQuestions'][number],
  answers: Record<string, string>,
): boolean {
  const anyAnswerText = Object.values(answers).map((value) => normalizeAdvisorText(String(value ?? ''))).join(' ');
  const hasAnyKeywordCondition = Array.isArray(question.activateWhenAnyAnswerIncludes) && question.activateWhenAnyAnswerIncludes.length > 0;
  const hasQuestionCondition = Array.isArray(question.activateWhenAnswersInclude) && question.activateWhenAnswersInclude.length > 0;

  if (!hasAnyKeywordCondition && !hasQuestionCondition) return true;

  const keywordPass = !hasAnyKeywordCondition || question.activateWhenAnyAnswerIncludes!.some((keyword) => anyAnswerText.includes(normalizeAdvisorText(keyword)));
  const questionPass = !hasQuestionCondition || question.activateWhenAnswersInclude!.some((rule) => {
    const answerText = getAdvisorAnswerText(answers, rule.questionId);
    return rule.keywords.some((keyword) => answerText.includes(normalizeAdvisorText(keyword)));
  });

  return keywordPass && questionPass;
}

function isAdvisorSignalRuleActive(
  rule: BusinessPackageArchetypePreset['advisorSignalRules'][number],
  answers: Record<string, string>,
  context?: AdvisorWorkspaceContext,
): boolean {
  const anyAnswerText = Object.values(answers).map((value) => normalizeAdvisorText(String(value ?? ''))).join(' ');
  const hasAnyKeywordCondition = Array.isArray(rule.whenAnyAnswerIncludes) && rule.whenAnyAnswerIncludes.length > 0;
  const hasQuestionCondition = Array.isArray(rule.whenAnswersInclude) && rule.whenAnswersInclude.length > 0;
  const hasContextCondition = Array.isArray(rule.whenContextMissing) && rule.whenContextMissing.length > 0;

  const keywordPass = !hasAnyKeywordCondition || rule.whenAnyAnswerIncludes!.some((keyword) => anyAnswerText.includes(normalizeAdvisorText(keyword)));
  const questionPass = !hasQuestionCondition || rule.whenAnswersInclude!.some((ruleEntry) => {
    const answerText = getAdvisorAnswerText(answers, ruleEntry.questionId);
    return ruleEntry.keywords.some((keyword) => answerText.includes(normalizeAdvisorText(keyword)));
  });
  const contextPass = !hasContextCondition || rule.whenContextMissing!.some((key) => {
    if (key === 'trustScope') return (context?.trustScopeLabels.length ?? 0) === 0;
    if (key === 'installMode') return !(context?.installMode ?? '').trim();
    if (key === 'riskClass') return !(context?.riskClass ?? '').trim();
    if (key === 'preflightReady') {
      const decision = normalizeAdvisorText(context?.latestPreflightDecision ?? '');
      return !['pass', 'passed', 'ready', 'approved'].includes(decision);
    }
    if (key === 'simulationReady') {
      const summary = normalizeAdvisorText(context?.latestSimulationSummary ?? '');
      return !summary || ['fail', 'failed', 'error', 'blocked', 'invalid'].some((keyword) => summary.includes(keyword));
    }
    return false;
  });

  return keywordPass && questionPass && contextPass;
}

function asAdvisorIssueArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object' && !Array.isArray(item));
}

function buildAdvisorDiagnosis(
  preset: BusinessPackageArchetypePreset,
  answers: Record<string, string>,
  context?: AdvisorWorkspaceContext,
): AdvisorDiagnosis {
  const normalizedAnswers = Object.fromEntries(
    Object.entries(answers).map(([key, value]) => [key, normalizeAdvisorText(String(value ?? ''))]),
  );
  const answerText = Object.values(normalizedAnswers).join(' ');
  const hasKeyword = (keywords: string[]) => keywords.some((keyword) => answerText.includes(keyword));
  const recommendedSurfaces = new Set<string>();
  const recommendedCapabilities = new Set<string>();
  const recommendedEntities = new Set<string>();
  const humanAssistMoments = new Set<string>();
  const riskFlags = new Set<string>();
  const complianceSignals = new Set<string>();
  const activeQuestions = preset.advisorQuestions.filter((question) => isAdvisorQuestionActive(question, answers));
  const missingQuestionItems = activeQuestions
    .filter((question) => !(normalizedAnswers[question.id] ?? '').trim())
    .map((question) => ({
      label: question.label,
      category: question.category ?? 'interview' as AdvisorChecklistCategory,
    }));
  const answered = activeQuestions.length - missingQuestionItems.length;

  let recommendedOverlay = Array.isArray((preset.interviewState as Record<string, unknown>).recommendedOverlayOptions)
    ? String(((preset.interviewState as Record<string, unknown>).recommendedOverlayOptions as unknown[])[0] ?? 'default')
    : 'default';
  let installShape = preset.id === 'finance_core' ? 'foundation_install' : 'tenant_install';

  if (preset.id === 'haulage') {
    recommendedSurfaces.add('haulage_dispatch_workspace');
    recommendedSurfaces.add('haulage_control_room');
    recommendedCapabilities.add('dispatch_planning');
    recommendedCapabilities.add('trip_monitoring');
    recommendedEntities.add('haulage_job');
    recommendedEntities.add('trip');
    recommendedEntities.add('assignment');
    recommendedEntities.add('customer');
    humanAssistMoments.add('低置信度派车建议由 dispatcher 最终确认');

    if (hasKeyword(['imp', 'import'])) recommendedOverlay = 'imp';
    else if (hasKeyword(['exp', 'export'])) recommendedOverlay = 'exp';
    else if (hasKeyword(['local', 'domestic'])) recommendedOverlay = 'loc';
    else if (hasKeyword(['roundtrip', 'round trip'])) recommendedOverlay = 'roundtrip';

    if (hasKeyword(['customer', 'eta', 'whatsapp', 'notify', 'portal'])) {
      recommendedSurfaces.add('customer_update_feed');
      recommendedCapabilities.add('customer_update_broadcast');
    }
    if (hasKeyword(['billing', 'invoice', 'finance', 'cost'])) {
      recommendedSurfaces.add('finance_foundation_workspace');
      recommendedCapabilities.add('billing_trigger');
      humanAssistMoments.add('billing-ready event 进入 finance review queue 后再过账');
    }
    if (hasKeyword(['psa', 'port', 'time window', 'timeslot', 'slot'])) {
      riskFlags.add('PSA/port 时窗依赖高，需 control room 升级面');
    }
    if (hasKeyword(['dg', 'dangerous', 'hazmat'])) {
      riskFlags.add('涉及 DG/危险品，不能只用默认 dispatch overlay');
    }
    if (hasKeyword(['driver', 'vehicle', 'shift', 'license', 'licence'])) {
      humanAssistMoments.add('司机/车辆/班次冲突需要人工 override');
    }
  } else if (preset.id === 'warehouse') {
    recommendedSurfaces.add('warehouse_planner_workspace');
    recommendedSurfaces.add('warehouse_task_queue');
    recommendedCapabilities.add('receiving');
    recommendedCapabilities.add('task_execution');
    recommendedCapabilities.add('inventory_visibility');
    recommendedEntities.add('warehouse_order');
    recommendedEntities.add('warehouse_task');
    recommendedEntities.add('inventory_unit');
    recommendedEntities.add('customer');
    humanAssistMoments.add('异常任务和库存调整由 supervisor 最终确认');

    if (hasKeyword(['3pl', 'third party'])) recommendedOverlay = 'three_pl';
    else if (hasKeyword(['project', 'project cargo'])) recommendedOverlay = 'project_cargo';
    else if (hasKeyword(['basic', 'stock'])) recommendedOverlay = 'basic_stock_control';

    if (hasKeyword(['scan', 'barcode', 'rf', 'scanner'])) {
      recommendedSurfaces.add('warehouse_scan_execution');
    }
    if (hasKeyword(['portal', 'share', 'customer', 'visibility'])) {
      recommendedSurfaces.add('customer_inventory_portal');
    }
    if (hasKeyword(['supervisor', 'queue', 'exception', 'damage', 'inspection'])) {
      recommendedSurfaces.add('warehouse_supervisor');
      riskFlags.add('仓内异常密度较高，需显式 exception queue');
    }
    if (hasKeyword(['billing', 'invoice', 'storage fee'])) {
      recommendedCapabilities.add('billing_hook');
      humanAssistMoments.add('storage/billing hook 先进入 finance review，再做正式 posting');
    }
  } else {
    recommendedSurfaces.add('finance_document_workspace');
    recommendedCapabilities.add('document_posting');
    recommendedEntities.add('invoice');
    recommendedEntities.add('payment');
    recommendedEntities.add('ledger_entry');
    recommendedEntities.add('counterparty');
    humanAssistMoments.add('posting override 和 close signoff 必须保留人工关口');

    if (hasKeyword(['observe'])) recommendedOverlay = 'observe_mode';
    else if (hasKeyword(['mirror'])) recommendedOverlay = 'erp_mirror_mode';
    else if (hasKeyword(['operate'])) recommendedOverlay = 'operate_mode';

    if (hasKeyword(['tax'])) {
      recommendedSurfaces.add('finance_tax_workspace');
      recommendedCapabilities.add('tax_control');
    }
    if (hasKeyword(['close', 'period'])) {
      recommendedSurfaces.add('finance_close_workspace');
      recommendedCapabilities.add('period_close');
    }
    if (hasKeyword(['recon', 'bank'])) {
      recommendedSurfaces.add('finance_recon_workspace');
      recommendedCapabilities.add('bank_reconciliation');
    }
    if (hasKeyword(['payment', 'bank', 'override'])) {
      humanAssistMoments.add('payment / bank exception 维持人工确认');
    }
    if (recommendedOverlay === 'observe_mode') {
      installShape = 'observe_then_mirror';
      riskFlags.add('旧 ERP 仍是主账，先不要把 vertical posting 直接切到 Boox');
    }
    if (recommendedOverlay === 'operate_mode') {
      riskFlags.add('切到 operate 前需要完成 close / recon / approval evidence 预演');
    }
  }

  const blockingGroups = createEmptyAdvisorGroups();
  const reviewGroups = createEmptyAdvisorGroups();
  const missingGroups = createEmptyAdvisorGroups();

  const pushBlocking = (category: AdvisorChecklistCategory, label: string) => {
    appendAdvisorGroup(blockingGroups, category, label);
    appendAdvisorGroup(missingGroups, category, label);
  };
  const pushReview = (category: AdvisorChecklistCategory, label: string) => {
    appendAdvisorGroup(reviewGroups, category, label);
    appendAdvisorGroup(missingGroups, category, label);
  };

  for (const item of missingQuestionItems) pushBlocking(item.category, item.label);

  if (!(context?.packageId ?? '').trim()) pushBlocking('data', 'Package ID 未定义');
  if (!(context?.intentSummary ?? '').trim()) pushBlocking('data', 'Intent summary 未定义');
  if ((context?.entityLabels.length ?? 0) === 0) pushBlocking('data', 'Domain entities 为空');
  if ((context?.capabilityLabels.length ?? 0) === 0) pushBlocking('data', 'Capabilities 为空');
  if ((context?.workspaceLabels.length ?? 0) === 0) pushBlocking('runtime', 'Workspace surfaces 为空');

  if (!(context?.installMode ?? '').trim()) pushReview('runtime', 'Install mode 尚未确认');
  if (!(context?.riskClass ?? '').trim()) pushReview('policy', 'Risk class 尚未确认');
  if ((context?.agentLabels.length ?? 0) === 0) pushReview('runtime', 'Agent bindings 尚未定义');
  if ((context?.trustScopeLabels.length ?? 0) === 0) pushReview('sharing', 'Trust scope 尚未定义');

  const preflightDecision = normalizeAdvisorText(context?.latestPreflightDecision ?? '');
  const simulationSummary = normalizeAdvisorText(context?.latestSimulationSummary ?? '');
  if (!preflightDecision) {
    pushReview('runtime', 'Preflight 尚未执行');
  } else if (['blocked', 'fail', 'failed', 'needs_changes', 'rejected'].includes(preflightDecision)) {
    pushBlocking('runtime', `Preflight 当前结果为 ${context?.latestPreflightDecision}`);
  } else if (!['pass', 'passed', 'ready', 'approved'].includes(preflightDecision)) {
    pushReview('runtime', `Preflight 结果为 ${context?.latestPreflightDecision}`);
  }

  for (const issue of context?.latestPreflightIssues ?? []) {
    const category = (typeof issue['category'] === 'string' ? issue['category'] : 'runtime') as AdvisorChecklistCategory;
    const label = typeof issue['label'] === 'string' ? issue['label'] : 'Preflight issue';
    const severity = typeof issue['severity'] === 'string' ? issue['severity'] : 'review';
    if (severity === 'blocking') pushBlocking(category, label);
    else pushReview(category, label);
  }

  if (!(context?.latestSimulationSummary ?? '').trim()) {
    pushReview('runtime', 'Simulation 尚未执行');
  } else if (['fail', 'failed', 'error', 'blocked', 'invalid'].some((keyword) => simulationSummary.includes(keyword))) {
    pushBlocking('runtime', `Simulation 提示异常：${context?.latestSimulationSummary}`);
  }
  for (const signal of context?.latestSimulationSignals ?? []) {
    const category = (typeof signal['category'] === 'string' ? signal['category'] : 'runtime') as AdvisorChecklistCategory;
    const label = typeof signal['label'] === 'string' ? signal['label'] : 'Simulation signal';
    const severity = typeof signal['severity'] === 'string' ? signal['severity'] : 'review';
    if (severity === 'blocking') pushBlocking(category, label);
    else pushReview(category, label);
  }
  for (const finding of context?.latestSimulationRiskFindings ?? []) {
    if (typeof finding['label'] === 'string') riskFlags.add(finding['label']);
  }

  for (const rule of preset.advisorSignalRules) {
    if (!isAdvisorSignalRuleActive(rule, answers, context)) continue;
    if (rule.kind === 'compliance') complianceSignals.add(rule.message);
    else if (rule.kind === 'risk') riskFlags.add(rule.message);
    else humanAssistMoments.add(rule.message);
  }

  if (hasKeyword(['customer', 'portal', 'share', 'visibility']) && (context?.trustScopeLabels.length ?? 0) === 0) {
    complianceSignals.add('涉及 customer/share 可见范围，但 trust scope 仍未定义');
  }
  if (hasKeyword(['approval', 'approve', 'override', 'exception'])) {
    complianceSignals.add('涉及审批或 override，需在 install 前确认 human boundary 与 approval gate');
  }
  if (hasKeyword(['dg', 'dangerous', 'hazmat', 'payment', 'bank', 'tax'])) {
    complianceSignals.add('涉及高风险业务动作，建议先完成 compliance review 与 simulation evidence');
  }
  if (recommendedCapabilities.has('billing_trigger') && !(context?.installMode ?? '').trim()) {
    complianceSignals.add('涉及 billing trigger，但 install mode 未明确，可能导致 posting 边界不清');
  }

  if (preset.id === 'haulage' && hasKeyword(['imp', 'exp', 'psa', 'port', 'slot']) && !hasKeyword(['approve', 'dispatcher', 'control room', 'review'])) {
    complianceSignals.add('拖车作业涉及港口/时窗，但未明确 control room 或 dispatcher 审核边界');
  }
  if (preset.id === 'warehouse' && hasKeyword(['customer', 'portal', 'share']) && !(context?.trustScopeLabels ?? []).some((scope) => ['tenant', 'group', 'platform'].includes(scope))) {
    complianceSignals.add('仓库客户可见范围仍不清晰，share / portal 上线前需确认 trust scope');
  }
  if (preset.id === 'finance_core' && hasKeyword(['operate', 'posting', 'payment']) && preflightDecision && !['pass', 'passed', 'ready', 'approved'].includes(preflightDecision)) {
    complianceSignals.add('Finance foundation 已接近 operate 模式，但 preflight 尚未到 ready/approved，不应直接 install');
  }

  const blockingItems = Object.values(blockingGroups).flat();
  const reviewItems = Object.values(reviewGroups).flat();
  const missingItems = [...blockingItems, ...reviewItems];
  const installGate = blockingItems.length > 0 ? 'blocked' : (reviewItems.length > 0 || complianceSignals.size > 0 ? 'review_required' : 'ready');

  const readiness = missingQuestionItems.length === 0 && blockingItems.filter((item) => !item.startsWith('Preflight') && !item.startsWith('Simulation')).length === 0
    ? 'blueprint_ready'
    : answered >= Math.max(1, Math.ceil(activeQuestions.length / 2))
      ? 'guided_ready'
      : 'needs_input';

  const nextActions = installGate === 'blocked'
    ? [
        `先补完：${blockingItems.slice(0, 2).join('；')}`,
        `保持 ${preset.label} blueprint，不要先改 raw manifest`,
        '完成问诊并补齐 manifest 核心对象后，再进入 install',
      ]
    : installGate === 'review_required'
      ? [
        '先完成 preflight + simulation',
        `补齐 review 项：${reviewItems.slice(0, 2).join('；') || 'compliance review'}`,
        '确认 human boundary、share scope、install mode 后再决定 install / exchange',
      ]
      : [
        `应用 ${preset.label} blueprint`,
        `按 ${recommendedOverlay} overlay 补齐 surface / capability / install plan`,
        '先做 preflight + simulation，再决定 install / exchange',
      ];

  const summary = installGate === 'blocked'
    ? `当前更像 ${preset.label} 的 ${recommendedOverlay} 方案，但仍有 ${blockingItems.length} 个 blocking 项。优先补齐后再进入 install。`
    : installGate === 'review_required'
      ? `当前方案已接近 ${preset.label} 的 ${recommendedOverlay} blueprint，但仍需完成 review/compliance 检查后再 install。`
      : `建议采用 ${preset.label} 的 ${recommendedOverlay} 方案，安装形态为 ${installShape}，并优先启用 ${Array.from(recommendedSurfaces).slice(0, 3).join('、')}。`;

  return {
    readiness,
    installGate,
    summary,
    recommendedOverlay,
    installShape,
    recommendedSurfaces: Array.from(recommendedSurfaces),
    recommendedCapabilities: Array.from(recommendedCapabilities),
    recommendedEntities: Array.from(recommendedEntities),
    humanAssistMoments: Array.from(humanAssistMoments),
    riskFlags: Array.from(riskFlags),
    complianceSignals: Array.from(complianceSignals),
    missingItems,
    blockingItems,
    reviewItems,
    blockingGroups,
    reviewGroups,
    missingGroups,
    nextActions,
  };
}

export default function PackManagerPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mergePageContext = useChatContextStore((state) => state.mergePageContext);
  const isPlatformAdmin = user?.role === 'platform_admin';
  const defaultTenantId = user?.tenantId ?? null;
  const [tenantId, setTenantId] = useState<string | null>(isPlatformAdmin ? null : defaultTenantId);
  const [loading, setLoading] = useState(false);
  const [packs, setPacks] = useState<PackItem[]>([]);
  const [drafts, setDrafts] = useState<ComposerDraft[]>([]);
  const [releases, setReleases] = useState<BusinessPackageRelease[]>([]);
  const [installs, setInstalls] = useState<PackageInstall[]>([]);
  const [exchangeListings, setExchangeListings] = useState<ExchangeListing[]>([]);
  const [configText, setConfigText] = useState<Record<string, string>>({});
  const [manifestText, setManifestText] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [listingNotes, setListingNotes] = useState<Record<string, string>>({});
  const [draftDetails, setDraftDetails] = useState<Record<string, DraftDetail | null>>({});
  const [releaseDetails, setReleaseDetails] = useState<Record<string, ReleaseDetail | null>>({});
  const [installDetails, setInstallDetails] = useState<Record<string, InstallDetail | null>>({});
  const [workspaceDraftId, setWorkspaceDraftId] = useState<string | null>(null);
  const [workspaceDraft, setWorkspaceDraft] = useState<DraftDetail | null>(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [workspaceManifestText, setWorkspaceManifestText] = useState('{}');
  const [workspaceInterviewText, setWorkspaceInterviewText] = useState('{}');
  const [advisorPresetId, setAdvisorPresetId] = useState<BusinessPackageArchetypePreset['id']>('haulage');
  const [selectedShowcaseId, setSelectedShowcaseId] = useState<string>(BUSINESS_PACKAGE_SHOWCASES[0]?.id ?? '');
  const [advisorQuestionIndex, setAdvisorQuestionIndex] = useState(0);
  const [workspaceTab, setWorkspaceTab] = useState('showcase');
  const [workspaceBaseline, setWorkspaceBaseline] = useState<{
    name: string;
    slug: string;
    manifestText: string;
    interviewText: string;
  } | null>(null);

  const canLoad = useMemo(() => {
    if (!isPlatformAdmin) return Boolean(defaultTenantId);
    return Boolean(tenantId);
  }, [isPlatformAdmin, tenantId, defaultTenantId]);

  useEffect(() => {
    if (!isPlatformAdmin) setTenantId(defaultTenantId);
  }, [isPlatformAdmin, defaultTenantId]);

  useEffect(() => {
    const showcase = getBusinessPackageShowcase(searchParams.get('showcase'));
    if (!showcase) return;
    setWorkspaceTab('showcase');
    setSelectedShowcaseId(showcase.id);
    setAdvisorPresetId(showcase.archetypePresetId);
    setAdvisorQuestionIndex(0);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('showcase');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!workspaceDraftId || !workspaceDraft) {
      mergePageContext({
        entityId: undefined,
        entityType: undefined,
        metadata: undefined,
      });
      return;
    }

    mergePageContext({
      entityId: workspaceDraftId,
      entityType: 'business_package_draft',
      metadata: {
        entityLabel: 'Business Package Draft',
        entityTitle: workspaceDraft.draft.name ?? workspaceDraft.draft.slug,
        draftStatus: workspaceDraft.draft.status,
        draftType: workspaceDraft.draft.draftType,
        preflightDecision: workspaceDraft.latestPreflight?.decision ?? null,
        simulationMode: workspaceDraft.latestSimulation?.mode ?? null,
        taskScopedSummary: `draft ${workspaceDraft.draft.status}; preflight ${workspaceDraft.latestPreflight?.decision ?? 'pending'}; simulation ${workspaceDraft.latestSimulation?.mode ?? 'pending'}`,
      },
      taskContext: {
        taskType: 'package_draft_review',
        taskGoal: 'Advance the draft through readiness, simulation, install, or exchange release.',
        currentStep: workspaceDraft.latestPreflight?.decision
          ? 'review_readiness'
          : 'prepare_draft',
        completionCriteria: [
          'Manifest and interview state are complete',
          'Preflight is not blocked',
          'Simulation or install path is explicitly chosen',
        ],
        missingItems: [
          ...((workspaceDraft.latestPreflight?.result?.issues ?? [])
            .filter((issue) => String(issue?.severity ?? '') === 'blocking')
            .map((issue) => String(issue?.label ?? 'Blocking issue'))),
        ],
        blockingRisks: [
          ...((workspaceDraft.latestPreflight?.result?.issues ?? [])
            .filter((issue) => String(issue?.severity ?? '') === 'blocking')
            .map((issue) => String(issue?.label ?? 'Blocking issue'))),
        ],
        recommendedNextActions: [
          workspaceDraft.latestPreflight?.decision === 'blocked'
            ? 'Resolve blocking issues in advisor/interview state'
            : 'Review preflight and simulation signals',
          workspaceDraft.latestSimulation?.mode
            ? 'Decide whether to install, validate, or share to exchange'
            : 'Run simulation before install/share',
        ],
      },
      executionContext: {
        workspace: 'package_manager',
        workspaceTab,
        draftStatus: workspaceDraft.draft.status,
        draftType: workspaceDraft.draft.draftType,
        preflightDecision: workspaceDraft.latestPreflight?.decision ?? null,
        simulationMode: workspaceDraft.latestSimulation?.mode ?? null,
      },
      permissionContext: {
        canEditDraft: true,
        canRunPreflight: true,
        canRunSimulation: true,
        canInstall: workspaceDraft.latestPreflight?.decision !== 'blocked',
        canShareToExchange: Boolean(workspaceDraft.derivedRelease?.id),
      },
      contextBudget: {
        detailLevel: 'medium',
        maxKeyFacts: 10,
        preferActionableSummary: true,
      },
    });

    return () => {
      mergePageContext({
        entityId: undefined,
        entityType: undefined,
        metadata: undefined,
        taskContext: undefined,
        executionContext: undefined,
        permissionContext: undefined,
        contextBudget: undefined,
      });
    };
  }, [
    mergePageContext,
    workspaceDraft,
    workspaceDraftId,
    workspaceTab,
  ]);

  const load = useCallback(async () => {
    if (!canLoad) {
      setPacks([]);
      return;
    }
    setLoading(true);
    try {
      const path = isPlatformAdmin ? `/plugins/tenant/${tenantId}` : '/plugins/tenant';
      const [data, draftData, releaseData, installData, exchangeData] = await Promise.all([
        api.get<{ items: PackItem[] }>(path),
        api.get<ComposerDraft[]>('/composer/drafts').catch(() => []),
        api.get<BusinessPackageRelease[]>('/business-packages').catch(() => []),
        api.get<PackageInstall[]>('/package-installs').catch(() => []),
        api.get<ExchangeListing[]>('/package-exchange/listings').catch(() => []),
      ]);
      const items = Array.isArray(data.items) ? data.items : [];
      setPacks(items);
      setDrafts(Array.isArray(draftData) ? draftData : []);
      setReleases(Array.isArray(releaseData) ? releaseData : []);
      setInstalls(Array.isArray(installData) ? installData : []);
      setExchangeListings(Array.isArray(exchangeData) ? exchangeData : []);
      const nextConfig: Record<string, string> = {};
      const nextManifest: Record<string, string> = {};
      for (const item of items) {
        nextConfig[item.id] = JSON.stringify(item.config ?? {}, null, 2);
        nextManifest[item.id] = JSON.stringify(item.manifest ?? {}, null, 2);
      }
      setConfigText(nextConfig);
      setManifestText(nextManifest);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [canLoad, isPlatformAdmin, tenantId, toast]);

  useEffect(() => { load(); }, [load]);

  const parseJson = (raw: string): Record<string, unknown> | null => {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const updateTenantPack = async (packId: string, payload: { enabled?: boolean; config?: Record<string, unknown> }) => {
    try {
      const path = isPlatformAdmin ? `/plugins/${packId}/tenant/${tenantId}` : `/plugins/${packId}/tenant`;
      await api.patch(path, payload);
      setPacks((prev) => prev.map((p) => (p.id === packId ? { ...p, ...payload } as PackItem : p)));
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    }
  };

  const updateManifest = async (packId: string, manifest: Record<string, unknown>) => {
    try {
      await api.patch(`/plugins/${packId}/manifest`, { manifest });
      setPacks((prev) => prev.map((p) => (p.id === packId ? { ...p, manifest } : p)));
      toast.success('默认 Manifest 已更新');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '更新失败');
    }
  };

  const shareReleaseToExchange = async (release: BusinessPackageRelease) => {
    setBusyKey(`share:${release.id}`);
    try {
      const listing = await api.post<{ id: string }>('/package-exchange/listings', {
        packageReleaseId: release.id,
        listingScope: 'group',
        discoverability: 'discoverable',
        approvalRequired: false,
      });
      await api.post(`/package-exchange/listings/${listing.id}/publish`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_exchange_share',
        units: 1,
        sourceType: 'package_release',
        sourceRef: release.id,
        notes: 'Release shared to exchange',
        metadata: { releaseId: release.id, listingId: listing.id, packageId: release.packageId, version: release.version },
      }).catch(() => undefined);
      toast.success('已发布到 Package Exchange');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '发布到 Exchange 失败');
    } finally {
      setBusyKey(null);
    }
  };

  const deprecateRelease = async (releaseId: string) => {
    setBusyKey(`deprecate:${releaseId}`);
    try {
      await api.post(`/business-packages/${releaseId}/deprecate`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_release_deprecate',
        units: 1,
        sourceType: 'package_release',
        sourceRef: releaseId,
        notes: 'Release deprecated',
        metadata: { releaseId },
      }).catch(() => undefined);
      toast.success('Release 已弃用');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Release 弃用失败');
    } finally {
      setBusyKey(null);
    }
  };

  const archiveListing = async (listingId: string) => {
    setBusyKey(`archive:${listingId}`);
    try {
      await api.post(`/package-exchange/listings/${listingId}/archive`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_exchange_archive',
        units: 1,
        sourceType: 'package_exchange',
        sourceRef: listingId,
        notes: 'Exchange listing archived',
        metadata: { listingId },
      }).catch(() => undefined);
      toast.success('Exchange listing 已下架');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Exchange listing 下架失败');
    } finally {
      setBusyKey(null);
    }
  };

  const refreshDraftDetailState = useCallback(async (draftId: string) => {
    const detail = await api.get<DraftDetail>(`/composer/drafts/${draftId}`);
    setDraftDetails((prev) => ({ ...prev, [draftId]: detail }));
    if (workspaceDraftId === draftId) {
      setWorkspaceDraft(detail);
      setWorkspaceName(detail.draft.name);
      setWorkspaceSlug(detail.draft.slug);
      setWorkspaceManifestText(JSON.stringify(detail.draft.manifest ?? {}, null, 2));
      setWorkspaceInterviewText(JSON.stringify(detail.draft.interviewState ?? {}, null, 2));
    }
    return detail;
  }, [workspaceDraftId]);

  const runDraftAction = async (
    draftId: string,
    action: 'compile' | 'preflight' | 'simulate' | 'install',
  ) => {
    setBusyKey(`draft:${action}:${draftId}`);
    try {
      if (action === 'compile') {
        await api.post(`/composer/drafts/${draftId}/compile`, {});
        setDraftNotes((prev) => ({ ...prev, [draftId]: 'compiled' }));
        await api.post('/billing/usage-events/consume', {
          actionKey: 'package_compile',
          units: 1,
          sourceType: 'composer_draft',
          sourceRef: draftId,
          notes: 'Composer draft compile metering',
          metadata: { draftId, action: 'compile' },
        }).catch(() => undefined);
      } else if (action === 'preflight') {
        const result = await api.post<{ decision?: string; recommendedNextAction?: string }>(
          `/composer/drafts/${draftId}/preflight`,
          {},
        );
        setDraftNotes((prev) => ({
          ...prev,
          [draftId]: `preflight:${result.decision ?? 'pass'} -> ${result.recommendedNextAction ?? 'next'}`,
        }));
        await api.post('/billing/usage-events/consume', {
          actionKey: 'package_preflight',
          units: 1,
          sourceType: 'composer_draft',
          sourceRef: draftId,
          notes: 'Composer draft preflight metering',
          metadata: { draftId, action: 'preflight' },
        }).catch(() => undefined);
      } else if (action === 'simulate') {
        const result = await api.post<{ summary?: string }>(
          `/composer/drafts/${draftId}/simulate`,
          { mode: 'schema_and_surface' },
        );
        setDraftNotes((prev) => ({ ...prev, [draftId]: result.summary ?? 'simulated' }));
        await api.post('/billing/usage-events/consume', {
          actionKey: 'package_simulation',
          units: 1,
          sourceType: 'composer_draft',
          sourceRef: draftId,
          notes: 'Composer draft simulation metering',
          metadata: { draftId, action: 'simulate', mode: 'schema_and_surface' },
        }).catch(() => undefined);
      } else {
        const result = await api.post<{ release?: { packageId?: string; version?: string }; install?: { id?: string } }>(
          `/composer/drafts/${draftId}/install`,
          {},
        );
        setDraftNotes((prev) => ({
          ...prev,
          [draftId]: `installed:${result.release?.packageId ?? 'package'}@${result.release?.version ?? '0.1.0'}`,
        }));
        await api.post('/billing/usage-events/consume', {
          actionKey: 'package_install',
          units: 1,
          sourceType: 'composer_install',
          sourceRef: result.install?.id ?? draftId,
          notes: 'Composer draft install metering',
          metadata: {
            draftId,
            packageId: result.release?.packageId ?? null,
            version: result.release?.version ?? null,
          },
        }).catch(() => undefined);
      }
      await refreshDraftDetailState(draftId).catch(() => undefined);
      toast.success(`Draft ${action} 完成`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Draft ${action} 失败`);
    } finally {
      setBusyKey(null);
    }
  };

  const toggleDraftDetail = async (draftId: string) => {
    if (draftDetails[draftId]) {
      setDraftDetails((prev) => ({ ...prev, [draftId]: null }));
      return;
    }
    setBusyKey(`draft:detail:${draftId}`);
    try {
      const detail = await api.get<DraftDetail>(`/composer/drafts/${draftId}`);
      setDraftDetails((prev) => ({ ...prev, [draftId]: detail }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载 draft 详情失败');
    } finally {
      setBusyKey(null);
    }
  };

  const openDraftWorkspace = async (draftId: string) => {
    setBusyKey(`draft:workspace:${draftId}`);
    try {
      const detail = await api.get<DraftDetail>(`/composer/drafts/${draftId}`);
      const manifestText = JSON.stringify(detail.draft.manifest ?? {}, null, 2);
      const interviewText = JSON.stringify(detail.draft.interviewState ?? {}, null, 2);
      setWorkspaceDraftId(draftId);
      setWorkspaceDraft(detail);
      setWorkspaceName(detail.draft.name);
      setWorkspaceSlug(detail.draft.slug);
      setWorkspaceManifestText(manifestText);
      setWorkspaceInterviewText(interviewText);
      const archetype = typeof detail.draft.manifest?.domain === 'object' && detail.draft.manifest?.domain && !Array.isArray(detail.draft.manifest.domain)
        ? (detail.draft.manifest.domain as Record<string, unknown>).archetype
        : undefined;
      if (archetype === 'warehouse') setAdvisorPresetId('warehouse');
      else if (archetype === 'finance_core') setAdvisorPresetId('finance_core');
      else setAdvisorPresetId('haulage');
      setAdvisorQuestionIndex(0);
      setWorkspaceBaseline({
        name: detail.draft.name,
        slug: detail.draft.slug,
        manifestText,
        interviewText,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '打开 draft workspace 失败');
    } finally {
      setBusyKey(null);
    }
  };

  const closeDraftWorkspace = () => {
    setWorkspaceDraftId(null);
    setWorkspaceDraft(null);
    setWorkspaceBaseline(null);
  };

  const saveDraftWorkspace = async () => {
    if (!workspaceDraftId) return;
    const manifest = parseJson(workspaceManifestText);
    const interviewState = parseJson(workspaceInterviewText);
    if (!manifest) {
      toast.error('Manifest JSON 无效');
      return;
    }
    if (!interviewState) {
      toast.error('Interview State JSON 无效');
      return;
    }

    setBusyKey(`draft:save:${workspaceDraftId}`);
    try {
      const updated = await api.patch<ComposerDraft>(`/composer/drafts/${workspaceDraftId}`, {
        name: workspaceName.trim(),
        slug: workspaceSlug.trim(),
        manifest,
        interviewState,
      });
      const manifestText = JSON.stringify(updated.manifest ?? manifest, null, 2);
      const interviewText = JSON.stringify(updated.interviewState ?? interviewState, null, 2);
      setWorkspaceName(updated.name);
      setWorkspaceSlug(updated.slug);
      setWorkspaceManifestText(manifestText);
      setWorkspaceInterviewText(interviewText);
      setWorkspaceBaseline({
        name: updated.name,
        slug: updated.slug,
        manifestText,
        interviewText,
      });
      setWorkspaceDraft((prev) => prev ? { ...prev, draft: updated } : prev);
      toast.success('Draft workspace 已保存');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存 draft workspace 失败');
    } finally {
      setBusyKey(null);
    }
  };

  const validateInstall = async (installId: string) => {
    setBusyKey(`validate:${installId}`);
    try {
      await api.post(`/package-installs/${installId}/validate`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_install_validate',
        units: 1,
        sourceType: 'package_install',
        sourceRef: installId,
        notes: 'Package install validate metering',
        metadata: { installId, action: 'validate' },
      }).catch(() => undefined);
      toast.success('Install 验证完成');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Install 验证失败');
    } finally {
      setBusyKey(null);
    }
  };

  const rollbackInstall = async (installId: string) => {
    setBusyKey(`rollback:${installId}`);
    try {
      await api.post(`/package-installs/${installId}/rollback`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_install_rollback',
        units: 1,
        sourceType: 'package_install',
        sourceRef: installId,
        notes: 'Package install rollback metering',
        metadata: { installId, action: 'rollback' },
      }).catch(() => undefined);
      toast.success('Install 已回滚');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Install 回滚失败');
    } finally {
      setBusyKey(null);
    }
  };

  const installFromExchange = async (listing: ExchangeListing) => {
    setBusyKey(`install:${listing.releaseId}`);
    try {
      await api.post(`/package-exchange/releases/${listing.releaseId}/install`, {});
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_install',
        units: 1,
        sourceType: 'package_exchange_install',
        sourceRef: listing.releaseId,
        notes: 'Exchange install metering',
        metadata: { listingId: listing.listingId, releaseId: listing.releaseId, action: 'install' },
      }).catch(() => undefined);
      toast.success('已从 Exchange 安装');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Exchange 安装失败');
    } finally {
      setBusyKey(null);
    }
  };

  const preflightExchangeListing = async (listing: ExchangeListing) => {
    setBusyKey(`preflight:${listing.releaseId}`);
    try {
      const result = await api.post<{ decision?: string; recommendedNextAction?: string }>(
        `/package-exchange/releases/${listing.releaseId}/preflight-install`,
        {},
      );
      setListingNotes((prev) => ({
        ...prev,
        [listing.listingId]: `preflight:${result.decision ?? 'pass'} -> ${result.recommendedNextAction ?? 'next'}`,
      }));
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_preflight',
        units: 1,
        sourceType: 'package_exchange',
        sourceRef: listing.releaseId,
        notes: 'Exchange preflight metering',
        metadata: { listingId: listing.listingId, releaseId: listing.releaseId, action: 'preflight' },
      }).catch(() => undefined);
      toast.success('Exchange preflight 完成');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Exchange preflight 失败');
    } finally {
      setBusyKey(null);
    }
  };

  const simulateExchangeListing = async (listing: ExchangeListing) => {
    setBusyKey(`simulate:${listing.releaseId}`);
    try {
      const result = await api.post<{ summary?: string }>(
        `/package-exchange/releases/${listing.releaseId}/simulate-install`,
        {},
      );
      setListingNotes((prev) => ({
        ...prev,
        [listing.listingId]: result.summary ?? 'simulation complete',
      }));
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_simulation',
        units: 1,
        sourceType: 'package_exchange',
        sourceRef: listing.releaseId,
        notes: 'Exchange simulation metering',
        metadata: { listingId: listing.listingId, releaseId: listing.releaseId, action: 'simulate' },
      }).catch(() => undefined);
      toast.success('Exchange simulate 完成');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Exchange simulate 失败');
    } finally {
      setBusyKey(null);
    }
  };

  const forkExchangeListing = async (listing: ExchangeListing) => {
    setBusyKey(`fork:${listing.releaseId}`);
    try {
      const result = await api.post<{ draft?: { id?: string; name?: string } }>(
        `/package-exchange/releases/${listing.releaseId}/fork`,
        {
          targetScope: 'tenant',
          newPackageName: `${listing.name ?? listing.packageId} Fork`,
          divergenceReason: `Forked from ${listing.packageId}@${listing.version}`,
        },
      );
      setListingNotes((prev) => ({
        ...prev,
        [listing.listingId]: `forked draft:${result.draft?.id?.slice(0, 8) ?? 'created'}`,
      }));
      await api.post('/billing/usage-events/consume', {
        actionKey: 'package_install',
        units: 1,
        sourceType: 'package_exchange_fork',
        sourceRef: result.draft?.id ?? listing.releaseId,
        notes: 'Exchange fork metering',
        metadata: { listingId: listing.listingId, releaseId: listing.releaseId, action: 'fork' },
      }).catch(() => undefined);
      toast.success('已从 Exchange fork 成 draft');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Exchange fork 失败');
    } finally {
      setBusyKey(null);
    }
  };

  const toggleReleaseDetail = async (releaseId: string) => {
    if (releaseDetails[releaseId]) {
      setReleaseDetails((prev) => ({ ...prev, [releaseId]: null }));
      return;
    }
    setBusyKey(`release-detail:${releaseId}`);
    try {
      const detail = await api.get<ReleaseDetail>(`/business-packages/${releaseId}`);
      setReleaseDetails((prev) => ({ ...prev, [releaseId]: detail }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载 release 详情失败');
    } finally {
      setBusyKey(null);
    }
  };

  const toggleInstallDetail = async (installId: string) => {
    if (installDetails[installId]) {
      setInstallDetails((prev) => ({ ...prev, [installId]: null }));
      return;
    }
    setBusyKey(`install-detail:${installId}`);
    try {
      const detail = await api.get<InstallDetail>(`/package-installs/${installId}`);
      setInstallDetails((prev) => ({ ...prev, [installId]: detail }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载 install 详情失败');
    } finally {
      setBusyKey(null);
    }
  };

  const isListingInstalled = useCallback(
    (releaseId: string) => installs.some(
      (install) => install.packageReleaseId === releaseId && install.status !== 'rolled_back',
    ),
    [installs],
  );

  const chainSummary = useMemo(() => {
    const draftReadyCount = drafts.filter((draft) => draft.status === 'ready_for_preflight' || draft.status === 'ready_for_install').length;
    const releaseDeprecatedCount = releases.filter((release) => release.status === 'deprecated').length;
    const activeInstallCount = installs.filter((install) => install.status !== 'rolled_back' && install.runtimeStatus === 'active').length;
    const exchangePublishedCount = exchangeListings.filter((listing) => listing.status === 'published').length;
    return {
      draftCount: drafts.length,
      draftReadyCount,
      releaseCount: releases.length,
      releaseDeprecatedCount,
      installCount: installs.length,
      activeInstallCount,
      exchangeCount: exchangeListings.length,
      exchangePublishedCount,
    };
  }, [drafts, releases, installs, exchangeListings]);

  const asRecord = useCallback((value: unknown): Record<string, unknown> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return { ...(value as Record<string, unknown>) };
  }, []);

  const slugifyToken = useCallback((value: string) => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, ''), []);

  const parseLineList = useCallback((raw: string) => raw
    .split(/\r?\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean), []);

  const extractStringArray = useCallback((value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
          const record = entry as Record<string, unknown>;
          const candidate = record.id ?? record.name ?? record.entity ?? record.targetSurface;
          return typeof candidate === 'string' ? candidate.trim() : '';
        }
        return '';
      })
      .filter(Boolean);
  }, []);

  const workspaceManifestValid = useMemo(() => parseJson(workspaceManifestText) !== null, [workspaceManifestText]);
  const workspaceInterviewValid = useMemo(() => parseJson(workspaceInterviewText) !== null, [workspaceInterviewText]);

  const workspaceManifest = useMemo<Record<string, unknown>>(
    () => parseJson(workspaceManifestText) ?? {},
    [workspaceManifestText],
  );
  const workspaceInterviewState = useMemo<Record<string, unknown>>(
    () => parseJson(workspaceInterviewText) ?? {},
    [workspaceInterviewText],
  );

  const updateWorkspaceManifest = useCallback((updater: (current: Record<string, unknown>) => Record<string, unknown>) => {
    const current = parseJson(workspaceManifestText) ?? {};
    const next = updater({ ...current });
    setWorkspaceManifestText(JSON.stringify(next, null, 2));
  }, [workspaceManifestText]);

  const updateWorkspaceInterview = useCallback((updater: (current: Record<string, unknown>) => Record<string, unknown>) => {
    const current = parseJson(workspaceInterviewText) ?? {};
    const next = updater({ ...current });
    setWorkspaceInterviewText(JSON.stringify(next, null, 2));
  }, [workspaceInterviewText]);

  const intentRecord = useMemo(() => asRecord(workspaceManifest.intent), [asRecord, workspaceManifest.intent]);
  const domainRecord = useMemo(() => asRecord(workspaceManifest.domain), [asRecord, workspaceManifest.domain]);
  const surfacesRecord = useMemo(() => asRecord(workspaceManifest.surfaces), [asRecord, workspaceManifest.surfaces]);
  const shareRecord = useMemo(() => asRecord(workspaceManifest.share), [asRecord, workspaceManifest.share]);
  const policiesRecord = useMemo(() => asRecord(workspaceManifest.policies), [asRecord, workspaceManifest.policies]);

  const capabilityLabels = useMemo(
    () => extractStringArray(workspaceManifest.capabilities),
    [extractStringArray, workspaceManifest.capabilities],
  );
  const workspaceLabels = useMemo(
    () => extractStringArray(surfacesRecord.workspaces),
    [extractStringArray, surfacesRecord.workspaces],
  );
  const agentLabels = useMemo(
    () => extractStringArray(workspaceManifest.agents),
    [extractStringArray, workspaceManifest.agents],
  );
  const entityLabels = useMemo(
    () => extractStringArray(domainRecord.entities),
    [domainRecord.entities, extractStringArray],
  );
  const outcomeLabels = useMemo(
    () => extractStringArray(intentRecord.outcomes),
    [extractStringArray, intentRecord.outcomes],
  );
  const personaLabels = useMemo(
    () => extractStringArray(intentRecord.personas),
    [extractStringArray, intentRecord.personas],
  );
  const taxonomyLabels = useMemo(
    () => extractStringArray(domainRecord.taxonomyTags),
    [domainRecord.taxonomyTags, extractStringArray],
  );
  const trustScopeLabels = useMemo(
    () => extractStringArray(workspaceManifest.trustScope),
    [extractStringArray, workspaceManifest.trustScope],
  );
  const advisorContext = useMemo<AdvisorWorkspaceContext>(() => ({
    packageId: typeof workspaceManifest.packageId === 'string' ? workspaceManifest.packageId : '',
    intentSummary: typeof intentRecord.summary === 'string' ? intentRecord.summary : '',
    riskClass: typeof policiesRecord.riskClass === 'string' ? policiesRecord.riskClass : '',
    installMode: typeof shareRecord.installMode === 'string' ? shareRecord.installMode : '',
    trustScopeLabels,
    capabilityLabels,
    workspaceLabels,
    agentLabels,
    entityLabels,
    latestPreflightDecision: workspaceDraft?.latestPreflight?.decision,
    latestPreflightNextAction: workspaceDraft?.latestPreflight?.result?.recommendedNextAction,
    latestSimulationMode: workspaceDraft?.latestSimulation?.mode,
    latestSimulationSummary: workspaceDraft?.latestSimulation?.result?.summary,
    latestPreflightIssues: asAdvisorIssueArray(workspaceDraft?.latestPreflight?.result?.issues),
    latestSimulationSignals: asAdvisorIssueArray(workspaceDraft?.latestSimulation?.result?.signals),
    latestSimulationScenarios: Array.isArray(workspaceDraft?.latestSimulation?.result?.mockScenarioRefs)
      ? workspaceDraft?.latestSimulation?.result?.mockScenarioRefs
      : [],
    latestSimulationRiskFindings: asAdvisorIssueArray(workspaceDraft?.latestSimulation?.result?.riskFindings),
  }), [
    workspaceManifest.packageId,
    intentRecord.summary,
    policiesRecord.riskClass,
    shareRecord.installMode,
    trustScopeLabels,
    capabilityLabels,
    workspaceLabels,
    agentLabels,
    entityLabels,
    workspaceDraft?.latestPreflight?.decision,
    workspaceDraft?.latestPreflight?.result?.recommendedNextAction,
    workspaceDraft?.latestPreflight?.result?.issues,
    workspaceDraft?.latestSimulation?.mode,
    workspaceDraft?.latestSimulation?.result?.summary,
    workspaceDraft?.latestSimulation?.result?.signals,
    workspaceDraft?.latestSimulation?.result?.mockScenarioRefs,
    workspaceDraft?.latestSimulation?.result?.riskFindings,
  ]);
  const selectedArchetypePreset = useMemo<BusinessPackageArchetypePreset>(
    () => BUSINESS_PACKAGE_ARCHETYPE_PRESETS.find((preset) => preset.id === advisorPresetId) ?? BUSINESS_PACKAGE_ARCHETYPE_PRESETS[0]!,
    [advisorPresetId],
  );
  const advisorAnswers = useMemo<Record<string, string>>(() => {
    const value = workspaceInterviewState.advisorAnswers;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, answer]) => typeof answer === 'string')
        .map(([key, answer]) => [key, String(answer)]),
    );
  }, [workspaceInterviewState.advisorAnswers]);
  const activeAdvisorQuestions = useMemo(
    () => selectedArchetypePreset.advisorQuestions.filter((question) => isAdvisorQuestionActive(question, advisorAnswers)),
    [advisorAnswers, selectedArchetypePreset.advisorQuestions],
  );
  const currentAdvisorQuestion = activeAdvisorQuestions[
    Math.min(advisorQuestionIndex, Math.max(activeAdvisorQuestions.length - 1, 0))
  ];
  useEffect(() => {
    if (advisorQuestionIndex >= activeAdvisorQuestions.length) {
      setAdvisorQuestionIndex(Math.max(activeAdvisorQuestions.length - 1, 0));
    }
  }, [activeAdvisorQuestions.length, advisorQuestionIndex]);
  const advisorCompletion = useMemo(() => {
    const answered = activeAdvisorQuestions.filter((question) => (advisorAnswers[question.id] ?? '').trim()).length;
    return {
      answered,
      total: activeAdvisorQuestions.length,
      missing: activeAdvisorQuestions
        .filter((question) => !(advisorAnswers[question.id] ?? '').trim())
        .map((question) => question.label),
    };
  }, [activeAdvisorQuestions, advisorAnswers]);
  const advisorDiagnosis = useMemo(
    () => buildAdvisorDiagnosis(selectedArchetypePreset, advisorAnswers, advisorContext),
    [advisorAnswers, advisorContext, selectedArchetypePreset],
  );
  const advisorRecommendation = useMemo(
    () => advisorDiagnosis.summary,
    [advisorDiagnosis.summary],
  );
  const workspaceInstallBlocked = advisorDiagnosis.installGate !== 'ready';
  const getAdvisorFieldState = useCallback((fieldKey: string): 'blocked' | 'review' | 'neutral' => {
    const blockedMap: Record<string, string[]> = {
      packageId: advisorDiagnosis.blockingGroups.data,
      'intent.summary': advisorDiagnosis.blockingGroups.data,
      'domain.entities': advisorDiagnosis.blockingGroups.data,
      capabilities: advisorDiagnosis.blockingGroups.data,
      'surfaces.workspaces': advisorDiagnosis.blockingGroups.runtime,
    };
    const reviewMap: Record<string, string[]> = {
      'share.installMode': advisorDiagnosis.reviewGroups.runtime,
      'policies.riskClass': advisorDiagnosis.reviewGroups.policy,
      trustScope: advisorDiagnosis.reviewGroups.sharing,
      agents: advisorDiagnosis.reviewGroups.runtime,
    };

    const blockedMatchers = blockedMap[fieldKey] ?? [];
    const reviewMatchers = reviewMap[fieldKey] ?? [];
    const isBlocked = blockedMatchers.some((item) =>
      (fieldKey === 'packageId' && item.includes('Package ID')) ||
      (fieldKey === 'intent.summary' && item.includes('Intent summary')) ||
      (fieldKey === 'domain.entities' && item.includes('Domain entities')) ||
      (fieldKey === 'capabilities' && item.includes('Capabilities')) ||
      (fieldKey === 'surfaces.workspaces' && item.includes('Workspace surfaces')));
    if (isBlocked) return 'blocked';
    const isReview = reviewMatchers.some((item) =>
      (fieldKey === 'share.installMode' && item.includes('Install mode')) ||
      (fieldKey === 'policies.riskClass' && item.includes('Risk class')) ||
      (fieldKey === 'trustScope' && item.includes('Trust scope')) ||
      (fieldKey === 'agents' && item.includes('Agent bindings')));
    return isReview ? 'review' : 'neutral';
  }, [advisorDiagnosis.blockingGroups.data, advisorDiagnosis.blockingGroups.runtime, advisorDiagnosis.reviewGroups.policy, advisorDiagnosis.reviewGroups.runtime, advisorDiagnosis.reviewGroups.sharing]);
  const advisorFieldClass = useCallback((fieldKey: string) => {
    const state = getAdvisorFieldState(fieldKey);
    if (state === 'blocked') return 'border-destructive/60 bg-destructive/5';
    if (state === 'review') return 'border-amber-300 bg-amber-50';
    return '';
  }, [getAdvisorFieldState]);

  const applyArchetypePreset = useCallback((preset: BusinessPackageArchetypePreset) => {
    const currentManifest = parseJson(workspaceManifestText) ?? {};
    const currentInterview = parseJson(workspaceInterviewText) ?? {};
    const presetDiagnosis = buildAdvisorDiagnosis(preset, {}, {
      packageId: typeof preset.manifest.packageId === 'string' ? preset.manifest.packageId : '',
      intentSummary: typeof (preset.manifest.intent as Record<string, unknown> | undefined)?.summary === 'string'
        ? String((preset.manifest.intent as Record<string, unknown>).summary)
        : '',
      riskClass: typeof (preset.manifest.policies as Record<string, unknown> | undefined)?.riskClass === 'string'
        ? String((preset.manifest.policies as Record<string, unknown>).riskClass)
        : '',
      installMode: typeof (preset.manifest.share as Record<string, unknown> | undefined)?.installMode === 'string'
        ? String((preset.manifest.share as Record<string, unknown>).installMode)
        : '',
      trustScopeLabels: Array.isArray(preset.manifest.trustScope) ? preset.manifest.trustScope.filter((item): item is string => typeof item === 'string') : [],
      capabilityLabels: Array.isArray(preset.manifest.capabilities)
        ? preset.manifest.capabilities.map((item) => {
          if (typeof item === 'string') return item;
          if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
          return typeof (item as Record<string, unknown>).id === 'string' ? String((item as Record<string, unknown>).id) : '';
        }).filter(Boolean)
        : [],
      workspaceLabels: Array.isArray((preset.manifest.surfaces as Record<string, unknown> | undefined)?.workspaces)
        ? (((preset.manifest.surfaces as Record<string, unknown>).workspaces) as unknown[]).filter((item): item is string => typeof item === 'string')
        : [],
      agentLabels: Array.isArray(preset.manifest.agents)
        ? preset.manifest.agents.map((item) => {
          if (typeof item === 'string') return item;
          if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
          return typeof (item as Record<string, unknown>).id === 'string' ? String((item as Record<string, unknown>).id) : '';
        }).filter(Boolean)
        : [],
      entityLabels: Array.isArray((preset.manifest.domain as Record<string, unknown> | undefined)?.entities)
        ? (((preset.manifest.domain as Record<string, unknown>).entities) as unknown[]).filter((item): item is string => typeof item === 'string')
        : [],
    });
    const mergedManifest: Record<string, unknown> = {
      ...preset.manifest,
      ownerScope: currentManifest.ownerScope ?? preset.manifest.ownerScope,
      ownerRef: currentManifest.ownerRef ?? preset.manifest.ownerRef,
      visibility: currentManifest.visibility ?? preset.manifest.visibility,
      trustScope: currentManifest.trustScope ?? preset.manifest.trustScope,
      version: currentManifest.version ?? preset.manifest.version,
      kind: currentManifest.kind ?? preset.manifest.kind,
      status: currentManifest.status ?? preset.manifest.status,
    };
    const mergedInterview: Record<string, unknown> = {
      ...preset.interviewState,
      ...currentInterview,
      archetype: preset.id,
      advisorRecommendation: presetDiagnosis.summary,
      advisorDiagnosis: presetDiagnosis,
    };

    setWorkspaceName((prev) => prev.trim() || preset.draftName);
    setWorkspaceSlug((prev) => prev.trim() || preset.draftSlug);
    setWorkspaceManifestText(JSON.stringify(mergedManifest, null, 2));
    setWorkspaceInterviewText(JSON.stringify(mergedInterview, null, 2));
    setAdvisorPresetId(preset.id);
    setAdvisorQuestionIndex(0);
  }, [workspaceInterviewText, workspaceManifestText]);

  const updateAdvisorAnswer = useCallback((questionId: string, answer: string) => {
    updateWorkspaceInterview((current) => {
      const nextAnswers = {
        ...asRecord(current.advisorAnswers),
        [questionId]: answer,
      };
      const normalizedAnswers = Object.fromEntries(
        Object.entries(nextAnswers).map(([key, value]) => [key, typeof value === 'string' ? value : String(value ?? '')]),
      ) as Record<string, string>;
      const nextDiagnosis = buildAdvisorDiagnosis(selectedArchetypePreset, normalizedAnswers, advisorContext);
      return {
        ...current,
        archetype: selectedArchetypePreset.id,
        advisorAnswers: nextAnswers,
        advisorRecommendation: nextDiagnosis.summary,
        advisorDiagnosis: nextDiagnosis,
      };
    });
  }, [advisorContext, asRecord, selectedArchetypePreset, updateWorkspaceInterview]);

  useEffect(() => {
    if (!workspaceInterviewValid) return;
    const currentSummary = typeof workspaceInterviewState.advisorRecommendation === 'string'
      ? workspaceInterviewState.advisorRecommendation
      : '';
    const currentDiagnosis = JSON.stringify(workspaceInterviewState.advisorDiagnosis ?? null);
    const nextDiagnosis = JSON.stringify(advisorDiagnosis);
    if (currentSummary === advisorRecommendation && currentDiagnosis === nextDiagnosis) return;
    updateWorkspaceInterview((current) => ({
      ...current,
      archetype: selectedArchetypePreset.id,
      advisorRecommendation,
      advisorDiagnosis,
    }));
  }, [
    advisorDiagnosis,
    advisorRecommendation,
    selectedArchetypePreset.id,
    updateWorkspaceInterview,
    workspaceInterviewState.advisorDiagnosis,
    workspaceInterviewState.advisorRecommendation,
    workspaceInterviewValid,
  ]);

  const navigationBindingLines = useMemo(() => {
    const bindings = Array.isArray(surfacesRecord.navigationBindings)
      ? surfacesRecord.navigationBindings
      : [];
    return bindings.map((binding) => {
      if (!binding || typeof binding !== 'object' || Array.isArray(binding)) return '';
      const record = binding as Record<string, unknown>;
      const entity = typeof record.entity === 'string' ? record.entity : 'entity';
      const action = typeof record.action === 'string' ? record.action : 'open';
      const targetSurface = typeof record.targetSurface === 'string' ? record.targetSurface : 'surface';
      return `${entity}:${action} -> ${targetSurface}`;
    }).filter(Boolean).join('\n');
  }, [surfacesRecord.navigationBindings]);

  const capabilityDependencyLines = useMemo(() => {
    const capabilities = Array.isArray(workspaceManifest.capabilities)
      ? workspaceManifest.capabilities
      : [];
    return capabilities.map((capability) => {
      if (!capability || typeof capability !== 'object' || Array.isArray(capability)) return '';
      const record = capability as Record<string, unknown>;
      const capabilityId = typeof record.id === 'string'
        ? record.id
        : (typeof record.name === 'string' ? slugifyToken(record.name) : '');
      if (!capabilityId) return '';
      const dependencies = extractStringArray(record.dependencies);
      return `${capabilityId} -> ${dependencies.join(', ')}`;
    }).filter(Boolean).join('\n');
  }, [extractStringArray, slugifyToken, workspaceManifest.capabilities]);

  const workspaceGraph = useMemo(() => {
    const nodes: WorkspaceGraphNode[] = [];
    const edges: WorkspaceGraphEdge[] = [];
    const packageNodeId = String(workspaceManifest.packageId ?? (workspaceSlug || 'draft-package'));

    nodes.push({
      id: packageNodeId,
      label: workspaceName || String(workspaceManifest.name ?? 'Draft Package'),
      kind: 'package',
    });

    const capabilities = Array.isArray(workspaceManifest.capabilities)
      ? workspaceManifest.capabilities
      : [];
    for (const capability of capabilities) {
      if (typeof capability === 'string') {
        nodes.push({ id: capability, label: capability, kind: 'capability' });
        edges.push({ from: packageNodeId, to: capability, label: 'contains' });
        continue;
      }
      if (!capability || typeof capability !== 'object' || Array.isArray(capability)) continue;
      const record = capability as Record<string, unknown>;
      const capabilityId = typeof record.id === 'string'
        ? record.id
        : (typeof record.name === 'string' ? slugifyToken(record.name) : '');
      if (!capabilityId) continue;
      nodes.push({
        id: capabilityId,
        label: typeof record.name === 'string' ? record.name : capabilityId,
        kind: 'capability',
      });
      edges.push({ from: packageNodeId, to: capabilityId, label: 'contains' });
      for (const dependency of extractStringArray(record.dependencies)) {
        const dependencyId = `dep:${dependency}`;
        nodes.push({ id: dependencyId, label: dependency, kind: 'dependency' });
        edges.push({ from: capabilityId, to: dependencyId, label: 'depends_on' });
      }
      for (const surfaceRef of extractStringArray(record.surfaceRefs)) {
        const surfaceId = `surface:${surfaceRef}`;
        nodes.push({ id: surfaceId, label: surfaceRef, kind: 'workspace' });
        edges.push({ from: capabilityId, to: surfaceId, label: 'renders_to' });
      }
      for (const agentRef of extractStringArray(record.agentBindingRefs)) {
        const agentId = `agent:${agentRef}`;
        nodes.push({ id: agentId, label: agentRef, kind: 'agent' });
        edges.push({ from: capabilityId, to: agentId, label: 'handled_by' });
      }
    }

    for (const workspaceRef of workspaceLabels) {
      nodes.push({ id: `surface:${workspaceRef}`, label: workspaceRef, kind: 'workspace' });
      edges.push({ from: packageNodeId, to: `surface:${workspaceRef}`, label: 'surface' });
    }

    const agents = Array.isArray(workspaceManifest.agents) ? workspaceManifest.agents : [];
    for (const agent of agents) {
      const agentId = typeof agent === 'string'
        ? agent
        : (agent && typeof agent === 'object' && !Array.isArray(agent) && typeof (agent as Record<string, unknown>).id === 'string'
          ? String((agent as Record<string, unknown>).id)
          : '');
      if (!agentId) continue;
      nodes.push({ id: `agent:${agentId}`, label: agentId, kind: 'agent' });
      edges.push({ from: packageNodeId, to: `agent:${agentId}`, label: 'agent' });
    }

    const bindings = Array.isArray(surfacesRecord.navigationBindings)
      ? surfacesRecord.navigationBindings
      : [];
    for (const binding of bindings) {
      if (!binding || typeof binding !== 'object' || Array.isArray(binding)) continue;
      const record = binding as Record<string, unknown>;
      const entity = typeof record.entity === 'string' ? record.entity : '';
      const targetSurface = typeof record.targetSurface === 'string' ? record.targetSurface : '';
      const action = typeof record.action === 'string' ? record.action : 'open';
      if (!entity || !targetSurface) continue;
      const entityNodeId = `entity:${entity}`;
      const targetNodeId = `surface:${targetSurface}`;
      nodes.push({ id: entityNodeId, label: entity, kind: 'entity' });
      nodes.push({ id: targetNodeId, label: targetSurface, kind: 'workspace' });
      edges.push({ from: entityNodeId, to: targetNodeId, label: action });
    }

    const dedupedNodes = Array.from(new Map(nodes.map((node) => [node.id, node])).values());
    const dedupedEdges = Array.from(new Map(edges.map((edge) => [`${edge.from}:${edge.to}:${edge.label}`, edge])).values());
    return { nodes: dedupedNodes, edges: dedupedEdges };
  }, [
    extractStringArray,
    slugifyToken,
    surfacesRecord.navigationBindings,
    workspaceLabels,
    workspaceManifest.agents,
    workspaceManifest.capabilities,
    workspaceManifest.name,
    workspaceManifest.packageId,
    workspaceName,
    workspaceSlug,
  ]);

  const workspaceDirty = useMemo(() => {
    if (!workspaceBaseline) return false;
    return (
      workspaceName !== workspaceBaseline.name ||
      workspaceSlug !== workspaceBaseline.slug ||
      workspaceManifestText !== workspaceBaseline.manifestText ||
      workspaceInterviewText !== workspaceBaseline.interviewText
    );
  }, [workspaceBaseline, workspaceName, workspaceSlug, workspaceManifestText, workspaceInterviewText]);

  const renderShowcasePanel = () => (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Business Package Demo Showcase</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          用这一页做完整演示：先选业务场景，再进入 Advisor/Guided/Builder，最后发布到 Exchange。这里不是静态说明，而是可直接触发下一步动作的 demo 控制台。
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-3">
        {BUSINESS_PACKAGE_SHOWCASES.map((showcase) => {
          const selected = selectedShowcaseId === showcase.id;
          return (
            <Card key={showcase.id} className={selected ? 'border-primary shadow-sm' : undefined}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  <span>{showcase.labelZh}</span>
                  <div className="flex items-center gap-1">
                    <Badge variant={showcase.patternLayer === 'foundation' ? 'default' : 'outline'}>
                      {showcase.patternLayer}
                    </Badge>
                    {selected && <Badge>selected</Badge>}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground">{showcase.summaryZh}</p>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span className="rounded-md border px-2 py-1">artifact: {showcase.builderArtifactType}</span>
                  {showcase.buildsOn ? <span className="rounded-md border px-2 py-1">builds on: {showcase.buildsOn}</span> : null}
                </div>
                <div className="rounded-md border bg-muted/20 px-3 py-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Outcome</p>
                  <p className="mt-1 text-sm">{showcase.outcomeZh}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Demo Flow</p>
                  <div className="mt-2 space-y-2">
                    {showcase.demoStepsZh.map((step, index) => (
                      <div key={step} className="rounded-md border px-3 py-2 text-sm">
                        <span className="mr-2 text-xs text-muted-foreground">{index + 1}.</span>
                        {step}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={selected ? 'default' : 'outline'}
                    onClick={() => {
                      setSelectedShowcaseId(showcase.id);
                      setAdvisorPresetId(showcase.archetypePresetId);
                      setAdvisorQuestionIndex(0);
                      setWorkspaceTab('advisor');
                    }}
                  >
                    Open Advisor
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setSelectedShowcaseId(showcase.id);
                      const preset = BUSINESS_PACKAGE_ARCHETYPE_PRESETS.find((item) => item.id === showcase.archetypePresetId);
                      if (preset) applyArchetypePreset(preset);
                      setWorkspaceTab('guided');
                    }}
                  >
                    Apply Blueprint
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedShowcaseId(showcase.id);
                      navigate(`/admin/builder?demo=${showcase.id}`);
                    }}
                  >
                    Open Builder
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100">
            <Package className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Business Package Manager</h1>
            <p className="text-sm text-muted-foreground">Composer drafts、package releases、exchange 与 installs 管理台</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isPlatformAdmin && (
            <AdminTenantScopeBar value={tenantId} onChange={setTenantId} />
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading || !canLoad} className="gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            刷新
          </Button>
        </div>
      </div>

      {!canLoad && isPlatformAdmin && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            请选择一个租户以查看/编辑该租户的 Pack 覆盖配置。
          </CardContent>
        </Card>
      )}

      {canLoad && packs.length === 0 && !loading && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">暂无 Pack 数据。</CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Draft Stage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{chainSummary.draftCount}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              ready: {chainSummary.draftReadyCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Release Stage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{chainSummary.releaseCount}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              deprecated: {chainSummary.releaseDeprecatedCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Exchange Stage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{chainSummary.exchangeCount}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              published: {chainSummary.exchangePublishedCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Runtime Stage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{chainSummary.installCount}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              active: {chainSummary.activeInstallCount}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Composer Drafts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {drafts.length === 0 ? (
              <div className="text-muted-foreground">暂无 draft 数据。</div>
            ) : drafts.slice(0, 8).map((draft) => (
              <div key={draft.id} className="rounded-md border p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{draft.name}</span>
                  <Badge variant="outline">{draft.draftType}</Badge>
                  <Badge>{draft.status}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono">
                  {draft.slug} · {draft.id.slice(0, 8)}
                </div>
                {draft.updatedAt && (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    updated: {new Date(draft.updatedAt).toLocaleString()}
                  </div>
                )}
                {draftNotes[draft.id] && (
                  <div className="mt-2 rounded bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
                    {draftNotes[draft.id]}
                  </div>
                )}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `draft:workspace:${draft.id}`}
                    onClick={() => openDraftWorkspace(draft.id)}
                  >
                    {busyKey === `draft:workspace:${draft.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Workspace
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `draft:detail:${draft.id}`}
                    onClick={() => toggleDraftDetail(draft.id)}
                  >
                    {busyKey === `draft:detail:${draft.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {draftDetails[draft.id] ? 'Hide Detail' : 'Detail'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `draft:compile:${draft.id}`}
                    onClick={() => runDraftAction(draft.id, 'compile')}
                  >
                    {busyKey === `draft:compile:${draft.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Compile
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `draft:preflight:${draft.id}`}
                    onClick={() => runDraftAction(draft.id, 'preflight')}
                  >
                    {busyKey === `draft:preflight:${draft.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Preflight
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `draft:simulate:${draft.id}`}
                    onClick={() => runDraftAction(draft.id, 'simulate')}
                  >
                    {busyKey === `draft:simulate:${draft.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Simulate
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-7 text-xs"
                    disabled={busyKey === `draft:install:${draft.id}`}
                    onClick={() => runDraftAction(draft.id, 'install')}
                  >
                    {busyKey === `draft:install:${draft.id}` ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Wand2 className="mr-1 h-3 w-3" />}
                    Install
                  </Button>
                </div>
                {draftDetails[draft.id] && (
                  <div className="mt-3 rounded-md bg-muted/40 p-2 text-[11px] space-y-2">
                    {draftDetails[draft.id]?.latestPreflight && (
                      <div>
                        preflight: {draftDetails[draft.id]?.latestPreflight?.decision ?? 'unknown'}
                        {draftDetails[draft.id]?.latestPreflight?.result?.recommendedNextAction
                          ? ` -> ${draftDetails[draft.id]?.latestPreflight?.result?.recommendedNextAction}`
                          : ''}
                      </div>
                    )}
                    {draftDetails[draft.id]?.latestSimulation?.result?.summary && (
                      <div>simulation: {draftDetails[draft.id]?.latestSimulation?.result?.summary}</div>
                    )}
                    {draftDetails[draft.id]?.derivedRelease && (
                      <div className="font-mono">
                        release: {draftDetails[draft.id]?.derivedRelease?.packageId}@{draftDetails[draft.id]?.derivedRelease?.version}
                      </div>
                    )}
                    {draftDetails[draft.id]?.recentInstalls && draftDetails[draft.id]!.recentInstalls!.length > 0 && (
                      <div>
                        installs:{' '}
                        {draftDetails[draft.id]!.recentInstalls!.slice(0, 3).map((install) => `${install.environment}/${install.runtimeStatus}`).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Package Releases</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {releases.length === 0 ? (
              <div className="text-muted-foreground">暂无 release 数据。</div>
            ) : releases.slice(0, 8).map((release) => (
              <div key={release.id} className="rounded-md border p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{String(release.manifest?.name ?? release.packageId)}</span>
                  <Badge variant="outline">{release.kind}</Badge>
                  <Badge>{release.status}</Badge>
                  {exchangeListings.some((listing) => listing.releaseId === release.id) && (
                    <Badge className="bg-emerald-100 text-emerald-700">exchange</Badge>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono">
                  {release.packageId}@{release.version}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {release.ownerScope}:{release.ownerRef} · {release.releaseChannel}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  installs: {release.installCount ?? 0} · active: {release.activeInstallCount ?? 0}
                </div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `release-detail:${release.id}`}
                    onClick={() => toggleReleaseDetail(release.id)}
                  >
                    {busyKey === `release-detail:${release.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {releaseDetails[release.id] ? 'Hide Detail' : 'Detail'}
                  </Button>
                  {!exchangeListings.some((listing) => listing.releaseId === release.id) && release.status !== 'deprecated' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 text-xs"
                      disabled={busyKey === `share:${release.id}`}
                      onClick={() => shareReleaseToExchange(release)}
                    >
                      {busyKey === `share:${release.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                      Share to Exchange
                    </Button>
                  )}
                  {release.status !== 'deprecated' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={busyKey === `deprecate:${release.id}`}
                      onClick={() => deprecateRelease(release.id)}
                    >
                      {busyKey === `deprecate:${release.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                      Deprecate
                    </Button>
                  )}
                </div>
                {releaseDetails[release.id] && (
                  <div className="mt-3 rounded-md bg-muted/40 p-2 text-[11px] space-y-2">
                    <div>
                      bindings:{' '}
                      {Object.entries(releaseDetails[release.id]?.runtimeBindingSummary ?? {}).map(([key, value]) => `${key} ${value.active}/${value.total}`).join(' · ') || 'none'}
                    </div>
                    {releaseDetails[release.id]?.exchangeListings && releaseDetails[release.id]!.exchangeListings!.length > 0 && (
                      <div>
                        exchange:{' '}
                        {releaseDetails[release.id]!.exchangeListings!.map((listing) => `${listing.scope}/${listing.status}`).join(' · ')}
                      </div>
                    )}
                    {releaseDetails[release.id]?.recentInstalls && releaseDetails[release.id]!.recentInstalls!.length > 0 && (
                      <div>
                        recent installs:{' '}
                        {releaseDetails[release.id]!.recentInstalls!.slice(0, 3).map((install) => `${install.environment}/${install.runtimeStatus}`).join(' · ')}
                      </div>
                    )}
                    {releaseDetails[release.id]?.lineage?.parents && releaseDetails[release.id]!.lineage!.parents!.length > 0 && (
                      <div>
                        parents:{' '}
                        {releaseDetails[release.id]!.lineage!.parents!.map((lineage) => `${lineage.packageId}@${lineage.version} (${lineage.lineageType})`).join(' · ')}
                      </div>
                    )}
                    {releaseDetails[release.id]?.lineage?.children && releaseDetails[release.id]!.lineage!.children!.length > 0 && (
                      <div>
                        children:{' '}
                        {releaseDetails[release.id]!.lineage!.children!.map((lineage) => `${lineage.packageId}@${lineage.version} (${lineage.lineageType})`).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Runtime Installs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {installs.length === 0 ? (
              <div className="text-muted-foreground">暂无 install 数据。</div>
            ) : installs.slice(0, 8).map((install) => (
              <div key={install.id} className="rounded-md border p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">
                    {String(install.effectiveManifest?.name ?? install.packageReleaseId)}
                  </span>
                  <Badge>{install.status}</Badge>
                  <Badge variant="outline">{install.runtimeStatus}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono">
                  {install.packageReleaseId.slice(0, 8)} · {install.id.slice(0, 8)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {install.environment} · {install.installMode}
                </div>
                {install.rollbackPointRef && (
                  <div className="mt-1 text-[11px] text-muted-foreground font-mono truncate">
                    {install.rollbackPointRef}
                  </div>
                )}
                {install.lastValidatedAt && (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    last validated: {new Date(install.lastValidatedAt).toLocaleString()}
                  </div>
                )}
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    disabled={busyKey === `install-detail:${install.id}`}
                    onClick={() => toggleInstallDetail(install.id)}
                  >
                    {busyKey === `install-detail:${install.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {installDetails[install.id] ? 'Hide Detail' : 'Detail'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-7 text-xs"
                    disabled={busyKey === `validate:${install.id}`}
                    onClick={() => validateInstall(install.id)}
                  >
                    {busyKey === `validate:${install.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Validate
                  </Button>
                  {install.status !== 'rolled_back' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={busyKey === `rollback:${install.id}`}
                      onClick={() => rollbackInstall(install.id)}
                    >
                      {busyKey === `rollback:${install.id}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                      Rollback
                    </Button>
                  )}
                </div>
                {installDetails[install.id] && (
                  <div className="mt-3 rounded-md bg-muted/40 p-2 text-[11px] space-y-2">
                    {installDetails[install.id]?.release && (
                      <div className="font-mono">
                        {installDetails[install.id]!.release!.packageId}@{installDetails[install.id]!.release!.version}
                      </div>
                    )}
                    {installDetails[install.id]?.runtimeBindings && installDetails[install.id]!.runtimeBindings!.length > 0 && (
                      <div>
                        bindings:{' '}
                        {installDetails[install.id]!.runtimeBindings!.slice(0, 4).map((binding) => `${binding.bindingType}:${binding.bindingRef}`).join(' · ')}
                      </div>
                    )}
                    {installDetails[install.id]?.recentEvents && installDetails[install.id]!.recentEvents!.length > 0 && (
                      <div>
                        events:{' '}
                        {installDetails[install.id]!.recentEvents!.slice(0, 3).map((event) => event.type).join(' · ')}
                      </div>
                    )}
                    {installDetails[install.id]?.recentRollbacks && installDetails[install.id]!.recentRollbacks!.length > 0 && (
                      <div>
                        rollbacks: {installDetails[install.id]!.recentRollbacks!.length}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Package Exchange</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {exchangeListings.length === 0 ? (
              <div className="text-muted-foreground">暂无 exchange listing 数据。</div>
            ) : exchangeListings.slice(0, 8).map((listing) => (
              <div key={listing.listingId} className="rounded-md border p-3">
                {(() => {
                  const installed = isListingInstalled(listing.releaseId);
                  return (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{listing.name ?? listing.packageId}</span>
                        <Badge>{listing.scope}</Badge>
                        <Badge variant="outline">{listing.kind}</Badge>
                        {listing.status && <Badge variant="outline">{listing.status}</Badge>}
                        {installed && <Badge className="bg-emerald-100 text-emerald-700">installed</Badge>}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground font-mono">
                        {listing.packageId}@{listing.version}
                      </div>
                      {listing.approvalRequired && (
                        <div className="mt-1 text-xs text-amber-600">install approval required</div>
                      )}
                      {listingNotes[listing.listingId] && (
                        <div className="mt-2 rounded bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
                          {listingNotes[listing.listingId]}
                        </div>
                      )}
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={busyKey === `preflight:${listing.releaseId}`}
                          onClick={() => preflightExchangeListing(listing)}
                        >
                          {busyKey === `preflight:${listing.releaseId}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          Preflight
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={busyKey === `simulate:${listing.releaseId}`}
                          onClick={() => simulateExchangeListing(listing)}
                        >
                          {busyKey === `simulate:${listing.releaseId}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          Simulate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={busyKey === `fork:${listing.releaseId}`}
                          onClick={() => forkExchangeListing(listing)}
                        >
                          {busyKey === `fork:${listing.releaseId}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          Fork
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-7 text-xs"
                          disabled={installed || busyKey === `install:${listing.releaseId}`}
                          onClick={() => installFromExchange(listing)}
                        >
                          {busyKey === `install:${listing.releaseId}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          {installed ? 'Installed' : 'Install'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={busyKey === `archive:${listing.listingId}`}
                          onClick={() => archiveListing(listing.listingId)}
                        >
                          {busyKey === `archive:${listing.listingId}` && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                          Archive
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        {renderShowcasePanel()}
      </div>

      <div className="space-y-4">
        {packs.map((pack) => {
          const manifest = pack.manifest ?? {};
          const vertical = typeof manifest.vertical === 'string' ? manifest.vertical : null;
          const packType = typeof manifest.type === 'string' ? manifest.type : 'plugin';
          const taskTypes = Array.isArray(manifest.taskTypes) ? manifest.taskTypes : [];
          return (
            <Card key={pack.id}>
              <CardHeader className="pb-2">
                <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                  <span>{pack.name}</span>
                  <Badge variant="secondary">{pack.id}</Badge>
                  <Badge className="bg-slate-100 text-slate-700">{packType}</Badge>
                  {vertical && <Badge className="bg-blue-100 text-blue-700">{vertical}</Badge>}
                  <span className="text-xs text-muted-foreground">v{pack.version}</span>
                </CardTitle>
                {pack.description && (
                  <p className="text-sm text-muted-foreground">{pack.description}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={Boolean(pack.enabled)}
                      onCheckedChange={(checked) => updateTenantPack(pack.id, { enabled: Boolean(checked) })}
                    />
                    <span className="text-sm">{pack.enabled ? '已启用' : '未启用'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Settings className="h-3.5 w-3.5" />
                    {taskTypes.length > 0 ? `${taskTypes.length} 个任务类型` : '未定义任务类型'}
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground">租户覆盖配置（JSON）</div>
                    <Textarea
                      value={configText[pack.id] ?? '{}'}
                      onChange={(e) => setConfigText((prev) => ({ ...prev, [pack.id]: e.target.value }))}
                      rows={8}
                      className="font-mono text-xs"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        const parsed = parseJson(configText[pack.id] ?? '');
                        if (!parsed) {
                          toast.error('配置 JSON 无效');
                          return;
                        }
                        updateTenantPack(pack.id, { config: parsed });
                      }}
                    >
                      保存覆盖配置
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-medium text-muted-foreground">
                      默认 Manifest（平台级）
                      {!isPlatformAdmin && <span className="ml-2 text-[11px] text-muted-foreground">只读</span>}
                    </div>
                    <Textarea
                      value={manifestText[pack.id] ?? '{}'}
                      onChange={(e) => setManifestText((prev) => ({ ...prev, [pack.id]: e.target.value }))}
                      rows={8}
                      className="font-mono text-xs"
                      readOnly={!isPlatformAdmin}
                    />
                    {isPlatformAdmin && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const parsed = parseJson(manifestText[pack.id] ?? '');
                          if (!parsed) {
                            toast.error('Manifest JSON 无效');
                            return;
                          }
                          updateManifest(pack.id, parsed);
                        }}
                      >
                        更新默认 Manifest
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Sheet open={Boolean(workspaceDraftId)} onOpenChange={(open) => { if (!open) closeDraftWorkspace(); }}>
        <SheetContent side="right" className="w-full sm:max-w-[1000px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Draft Workspace</SheetTitle>
            <SheetDescription>
              编辑 draft 元数据、manifest 与 interview state，然后继续 compile / preflight / simulate / install。
            </SheetDescription>
          </SheetHeader>
          {workspaceDraft && (
            <div className="mt-6 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{workspaceDraft.draft.draftType}</Badge>
                <Badge>{workspaceDraft.draft.status}</Badge>
                {workspaceDirty && <Badge variant="secondary">unsaved changes</Badge>}
                {workspaceDraft.latestPreflight?.decision && (
                  <Badge variant="outline">preflight: {workspaceDraft.latestPreflight.decision}</Badge>
                )}
                {workspaceDraft.latestSimulation?.mode && (
                  <Badge variant="outline">simulation: {workspaceDraft.latestSimulation.mode}</Badge>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="draft-workspace-name">Name</Label>
                  <Input
                    id="draft-workspace-name"
                    value={workspaceName}
                    onChange={(event) => setWorkspaceName(event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="draft-workspace-slug">Slug</Label>
                  <Input
                    id="draft-workspace-slug"
                    value={workspaceSlug}
                    onChange={(event) => setWorkspaceSlug(event.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={!workspaceDirty || !workspaceDraftId || busyKey === `draft:save:${workspaceDraftId}`}
                  onClick={saveDraftWorkspace}
                >
                  {workspaceDraftId && busyKey === `draft:save:${workspaceDraftId}` && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Save Draft
                </Button>
                <Button
                  variant="outline"
                  disabled={!workspaceDraftId || busyKey === `draft:compile:${workspaceDraftId}`}
                  onClick={() => workspaceDraftId && runDraftAction(workspaceDraftId, 'compile')}
                >
                  {workspaceDraftId && busyKey === `draft:compile:${workspaceDraftId}` && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Compile
                </Button>
                <Button
                  variant="outline"
                  disabled={!workspaceDraftId || busyKey === `draft:preflight:${workspaceDraftId}`}
                  onClick={() => workspaceDraftId && runDraftAction(workspaceDraftId, 'preflight')}
                >
                  {workspaceDraftId && busyKey === `draft:preflight:${workspaceDraftId}` && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Preflight
                </Button>
                <Button
                  variant="outline"
                  disabled={!workspaceDraftId || busyKey === `draft:simulate:${workspaceDraftId}`}
                  onClick={() => workspaceDraftId && runDraftAction(workspaceDraftId, 'simulate')}
                >
                  {workspaceDraftId && busyKey === `draft:simulate:${workspaceDraftId}` && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Simulate
                </Button>
                <Button
                  disabled={!workspaceDraftId || workspaceInstallBlocked || busyKey === `draft:install:${workspaceDraftId}`}
                  onClick={() => workspaceDraftId && runDraftAction(workspaceDraftId, 'install')}
                >
                  {workspaceDraftId && busyKey === `draft:install:${workspaceDraftId}` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1 h-4 w-4" />}
                  Install
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={advisorDiagnosis.installGate === 'ready' ? 'default' : advisorDiagnosis.installGate === 'review_required' ? 'secondary' : 'destructive'}>
                  install gate: {advisorDiagnosis.installGate}
                </Badge>
                {workspaceInstallBlocked && (
                  <span className="text-destructive">
                    Advisor 检测到 blocking 项，当前 draft 不允许直接 install。
                  </span>
                )}
              </div>

              <Tabs value={workspaceTab} onValueChange={setWorkspaceTab} className="space-y-3">
                <TabsList className="flex h-auto flex-wrap">
                  <TabsTrigger value="showcase">Showcase</TabsTrigger>
                  <TabsTrigger value="advisor">Advisor</TabsTrigger>
                  <TabsTrigger value="guided">Guided</TabsTrigger>
                  <TabsTrigger value="structure">Structure</TabsTrigger>
                  <TabsTrigger value="graph">Graph</TabsTrigger>
                  <TabsTrigger value="manifest">Manifest JSON</TabsTrigger>
                  <TabsTrigger value="interview">Interview</TabsTrigger>
                  <TabsTrigger value="discussion">Discussion</TabsTrigger>
                  <TabsTrigger value="ops">Ops</TabsTrigger>
                </TabsList>

                <TabsContent value="showcase" className="space-y-4">
                  {renderShowcasePanel()}
                </TabsContent>

                <TabsContent value="advisor" className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    {BUSINESS_PACKAGE_ARCHETYPE_PRESETS.map((preset) => (
                      <Card
                        key={preset.id}
                        className={advisorPresetId === preset.id ? 'border-primary shadow-sm' : undefined}
                      >
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center justify-between text-base">
                            <span>{preset.label}</span>
                            {advisorPresetId === preset.id && <Badge>selected</Badge>}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                          <p className="text-muted-foreground">{preset.summary}</p>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Personas</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {preset.personas.map((persona) => (
                                <Badge key={persona} variant="outline">{persona}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={advisorPresetId === preset.id ? 'default' : 'outline'}
                              onClick={() => {
                                setAdvisorPresetId(preset.id);
                                setAdvisorQuestionIndex(0);
                              }}
                            >
                              Review
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => applyArchetypePreset(preset)}
                            >
                              Apply Blueprint
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Recommended Next Steps</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {selectedArchetypePreset.nextSteps.map((step) => (
                          <div key={step} className="rounded-md border px-3 py-2">
                            {step}
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Interview Checklist</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {selectedArchetypePreset.interviewChecklist.map((step) => (
                          <div key={step} className="rounded-md border px-3 py-2">
                            {step}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Advisor Interview Flow</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">
                          {advisorCompletion.answered}/{advisorCompletion.total} answered
                        </Badge>
                        <Badge>{selectedArchetypePreset.label}</Badge>
                        <Badge variant="secondary">{activeAdvisorQuestions.length} active questions</Badge>
                      </div>

                      {currentAdvisorQuestion && (
                        <div className="rounded-lg border p-4 space-y-3">
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Question {advisorQuestionIndex + 1}
                            </p>
                            <p className="mt-1 text-sm font-medium">{currentAdvisorQuestion.label}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{currentAdvisorQuestion.helper}</p>
                          </div>
                          <Textarea
                            rows={currentAdvisorQuestion.answerMode === 'long' ? 5 : 3}
                            value={advisorAnswers[currentAdvisorQuestion.id] ?? ''}
                            onChange={(event) => updateAdvisorAnswer(currentAdvisorQuestion.id, event.target.value)}
                            placeholder="记录当前业务答案，Composer 会把它写入 interview state。"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={advisorQuestionIndex === 0}
                              onClick={() => setAdvisorQuestionIndex((prev) => Math.max(prev - 1, 0))}
                            >
                              Previous
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={advisorQuestionIndex >= activeAdvisorQuestions.length - 1}
                              onClick={() => setAdvisorQuestionIndex((prev) => Math.min(prev + 1, activeAdvisorQuestions.length - 1))}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="rounded-lg border border-dashed p-4 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">Advisor Recommendation</p>
                          <Badge variant={advisorDiagnosis.readiness === 'blueprint_ready' ? 'default' : 'outline'}>
                            {advisorDiagnosis.readiness}
                          </Badge>
                          <Badge variant={advisorDiagnosis.installGate === 'ready' ? 'default' : advisorDiagnosis.installGate === 'review_required' ? 'secondary' : 'destructive'}>
                            {advisorDiagnosis.installGate}
                          </Badge>
                          <Badge variant="secondary">{advisorDiagnosis.recommendedOverlay}</Badge>
                          <Badge variant="outline">{advisorDiagnosis.installShape}</Badge>
                        </div>
                        <p className="mt-2 text-muted-foreground">{advisorRecommendation}</p>
                        {advisorCompletion.missing.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {advisorCompletion.missing.map((question) => (
                              <Badge key={question} variant="outline">{question}</Badge>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recommended Surfaces</CardTitle>
                          </CardHeader>
                          <CardContent className="flex flex-wrap gap-2">
                            {advisorDiagnosis.recommendedSurfaces.map((surface) => (
                              <Badge key={surface} variant="outline">{surface}</Badge>
                            ))}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recommended Capabilities</CardTitle>
                          </CardHeader>
                          <CardContent className="flex flex-wrap gap-2">
                            {advisorDiagnosis.recommendedCapabilities.map((capability) => (
                              <Badge key={capability} variant="outline">{capability}</Badge>
                            ))}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recommended Entities</CardTitle>
                          </CardHeader>
                          <CardContent className="flex flex-wrap gap-2">
                            {advisorDiagnosis.recommendedEntities.map((entity) => (
                              <Badge key={entity} variant="outline">{entity}</Badge>
                            ))}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Human Boundary</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm text-muted-foreground">
                            {advisorDiagnosis.humanAssistMoments.map((item) => (
                              <div key={item} className="rounded-md border px-3 py-2">
                                {item}
                              </div>
                            ))}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Risk & Next Actions</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            {advisorDiagnosis.riskFlags.length > 0 ? (
                              advisorDiagnosis.riskFlags.map((risk) => (
                                <div key={risk} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                                  {risk}
                                </div>
                              ))
                            ) : (
                              <div className="rounded-md border px-3 py-2 text-muted-foreground">
                                当前没有额外高风险信号，先按 blueprint 预演。
                              </div>
                            )}
                            {advisorDiagnosis.nextActions.map((step) => (
                              <div key={step} className="rounded-md border px-3 py-2 text-muted-foreground">
                                {step}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <Card className={advisorDiagnosis.blockingItems.length > 0 ? 'border-destructive/40' : undefined}>
                          <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-sm">
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                              Blocking Items
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            {advisorDiagnosis.blockingItems.length > 0 ? Object.entries(advisorDiagnosis.blockingGroups).map(([category, items]) => (
                              items.length > 0 ? (
                                <div key={category} className="space-y-2">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    {ADVISOR_CATEGORY_LABELS[category as AdvisorChecklistCategory]}
                                  </p>
                                  {items.map((item) => (
                                    <div key={item} className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2">
                                      {item}
                                    </div>
                                  ))}
                                </div>
                              ) : null
                            )) : (
                              <div className="rounded-md border px-3 py-2 text-muted-foreground">
                                当前没有 blocking 项。
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <Card className={advisorDiagnosis.reviewItems.length > 0 ? 'border-amber-300' : undefined}>
                          <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-sm">
                              <Settings className="h-4 w-4 text-amber-600" />
                              Review Items
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            {advisorDiagnosis.reviewItems.length > 0 ? Object.entries(advisorDiagnosis.reviewGroups).map(([category, items]) => (
                              items.length > 0 ? (
                                <div key={category} className="space-y-2">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    {ADVISOR_CATEGORY_LABELS[category as AdvisorChecklistCategory]}
                                  </p>
                                  {items.map((item) => (
                                    <div key={item} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                                      {item}
                                    </div>
                                  ))}
                                </div>
                              ) : null
                            )) : (
                              <div className="rounded-md border px-3 py-2 text-muted-foreground">
                                当前没有额外 review 项。
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <Card className={advisorDiagnosis.complianceSignals.length > 0 ? 'border-amber-300' : undefined}>
                          <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-sm">
                              <ShieldAlert className="h-4 w-4 text-amber-600" />
                              Compliance Signals
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            {advisorDiagnosis.complianceSignals.length > 0 ? advisorDiagnosis.complianceSignals.map((item) => (
                              <div key={item} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                                {item}
                              </div>
                            )) : (
                              <div className="rounded-md border px-3 py-2 text-muted-foreground">
                                当前没有额外 compliance signal。
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Runtime Feedback</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-3 text-sm">
                          <div className="rounded-md border px-3 py-2">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Preflight</p>
                            <p className="mt-1">{workspaceDraft?.latestPreflight?.decision ?? 'not run'}</p>
                            {workspaceDraft?.latestPreflight?.result?.recommendedNextAction && (
                              <p className="mt-1 text-muted-foreground">
                                {workspaceDraft.latestPreflight.result.recommendedNextAction}
                              </p>
                            )}
                          </div>
                          <div className="rounded-md border px-3 py-2">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Simulation</p>
                            <p className="mt-1">{workspaceDraft?.latestSimulation?.result?.summary ?? 'not run'}</p>
                            {workspaceDraft?.latestSimulation?.mode && (
                              <p className="mt-1 text-muted-foreground">mode: {workspaceDraft.latestSimulation.mode}</p>
                            )}
                          </div>
                          <div className="rounded-md border px-3 py-2">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Scenario Evidence</p>
                            <div className="mt-1 space-y-1 text-muted-foreground">
                              {Array.isArray(workspaceDraft?.latestSimulation?.result?.mockScenarioRefs) && workspaceDraft.latestSimulation.result.mockScenarioRefs.length > 0 ? (
                                workspaceDraft.latestSimulation.result.mockScenarioRefs.slice(0, 3).map((scenario) => (
                                  <div key={scenario}>scenario: {scenario}</div>
                                ))
                              ) : (
                                <div>not generated</div>
                              )}
                              {Array.isArray(workspaceDraft?.latestSimulation?.result?.approvalCheckpoints) && workspaceDraft.latestSimulation.result.approvalCheckpoints.length > 0 && (
                                <div>approval: {workspaceDraft.latestSimulation.result.approvalCheckpoints.slice(0, 2).join(', ')}</div>
                              )}
                              {Array.isArray(workspaceDraft?.latestSimulation?.result?.riskFindings) && workspaceDraft.latestSimulation.result.riskFindings.length > 0 && (
                                <div>risk: {String(workspaceDraft.latestSimulation.result.riskFindings[0]?.label ?? 'risk finding')}</div>
                              )}
                              {Array.isArray(workspaceDraft?.latestSimulation?.result?.operatorNotes) && workspaceDraft.latestSimulation.result.operatorNotes.length > 0 && (
                                <div>note: {workspaceDraft.latestSimulation.result.operatorNotes[0]}</div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Blueprint Intent Preview</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Target draft:</span>{' '}
                        <code>{selectedArchetypePreset.draftSlug}</code>
                      </div>
                      <div>
                        <span className="font-medium">Manifest package:</span>{' '}
                        <code>{String(selectedArchetypePreset.manifest.packageId ?? selectedArchetypePreset.draftSlug)}</code>
                      </div>
                      <div className="text-muted-foreground">
                        应用 blueprint 后，`Guided` 会变成业务顾问层，`Structure` 管 capability/surface/agent，`Graph` 只保留给专家和实施人员。
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="guided" className="space-y-4">
                  {!workspaceManifestValid && (
                    <Card>
                      <CardContent className="p-4 text-sm text-amber-700">
                        Manifest JSON 当前无效。请先在 `Manifest JSON` 页签修复，再使用引导式编辑。
                      </CardContent>
                    </Card>
                  )}
                  {workspaceManifestValid && (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Identity</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="draft-package-id">Package ID</Label>
                              <Input
                                id="draft-package-id"
                                className={advisorFieldClass('packageId')}
                                value={typeof workspaceManifest.packageId === 'string' ? workspaceManifest.packageId : ''}
                                onChange={(event) => updateWorkspaceManifest((current) => ({
                                  ...current,
                                  packageId: event.target.value.trim(),
                                }))}
                              />
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="draft-kind">Kind</Label>
                                <Input
                                  id="draft-kind"
                                  value={typeof workspaceManifest.kind === 'string' ? workspaceManifest.kind : ''}
                                  onChange={(event) => updateWorkspaceManifest((current) => ({
                                    ...current,
                                    kind: event.target.value.trim(),
                                  }))}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="draft-version">Version</Label>
                                <Input
                                  id="draft-version"
                                  value={typeof workspaceManifest.version === 'string' ? workspaceManifest.version : ''}
                                  onChange={(event) => updateWorkspaceManifest((current) => ({
                                    ...current,
                                    version: event.target.value.trim(),
                                  }))}
                                />
                              </div>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="draft-owner-scope">Owner Scope</Label>
                                <Input
                                  id="draft-owner-scope"
                                  value={typeof workspaceManifest.ownerScope === 'string' ? workspaceManifest.ownerScope : ''}
                                  onChange={(event) => updateWorkspaceManifest((current) => ({
                                    ...current,
                                    ownerScope: event.target.value.trim(),
                                  }))}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="draft-visibility">Visibility</Label>
                                <Input
                                  id="draft-visibility"
                                  value={typeof workspaceManifest.visibility === 'string' ? workspaceManifest.visibility : ''}
                                  onChange={(event) => updateWorkspaceManifest((current) => ({
                                    ...current,
                                    visibility: event.target.value.trim(),
                                  }))}
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="draft-description">Description</Label>
                              <Textarea
                                id="draft-description"
                                rows={4}
                                value={typeof workspaceManifest.description === 'string' ? workspaceManifest.description : ''}
                                onChange={(event) => updateWorkspaceManifest((current) => ({
                                  ...current,
                                  description: event.target.value,
                                }))}
                              />
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Intent & Domain</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="draft-intent-summary">Intent Summary</Label>
                              <Textarea
                                id="draft-intent-summary"
                                rows={4}
                                className={advisorFieldClass('intent.summary')}
                                value={typeof intentRecord.summary === 'string' ? intentRecord.summary : ''}
                                onChange={(event) => updateWorkspaceManifest((current) => ({
                                  ...current,
                                  intent: {
                                    ...asRecord(current.intent),
                                    summary: event.target.value,
                                  },
                                }))}
                              />
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="draft-archetype">Archetype</Label>
                                <Input
                                  id="draft-archetype"
                                  value={typeof domainRecord.archetype === 'string' ? domainRecord.archetype : ''}
                                  onChange={(event) => updateWorkspaceManifest((current) => ({
                                    ...current,
                                    domain: {
                                      ...asRecord(current.domain),
                                      archetype: event.target.value.trim(),
                                    },
                                  }))}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="draft-risk-class">Risk Class</Label>
                                <Input
                                  id="draft-risk-class"
                                  className={advisorFieldClass('policies.riskClass')}
                                  value={typeof policiesRecord.riskClass === 'string' ? policiesRecord.riskClass : ''}
                                  onChange={(event) => updateWorkspaceManifest((current) => ({
                                    ...current,
                                    policies: {
                                      ...asRecord(current.policies),
                                      riskClass: event.target.value.trim(),
                                    },
                                  }))}
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="draft-trust-scope">Trust Scope</Label>
                              <Textarea
                                id="draft-trust-scope"
                                rows={4}
                                className={advisorFieldClass('trustScope')}
                                value={trustScopeLabels.join('\n')}
                                onChange={(event) => updateWorkspaceManifest((current) => ({
                                  ...current,
                                  trustScope: parseLineList(event.target.value),
                                }))}
                              />
                              <p className="text-[11px] text-muted-foreground">每行一个 scope，例如 `platform`、`group`、`tenant`。</p>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Share & Runtime</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label htmlFor="draft-install-mode">Install Mode</Label>
                            <Input
                              id="draft-install-mode"
                              className={advisorFieldClass('share.installMode')}
                              value={typeof shareRecord.installMode === 'string' ? shareRecord.installMode : ''}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                share: {
                                  ...asRecord(current.share),
                                  installMode: event.target.value.trim(),
                                },
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="draft-upgrade-channel">Upgrade Channel</Label>
                            <Input
                              id="draft-upgrade-channel"
                              value={typeof shareRecord.upgradeChannel === 'string' ? shareRecord.upgradeChannel : ''}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                share: {
                                  ...asRecord(current.share),
                                  upgradeChannel: event.target.value.trim(),
                                },
                              }))}
                            />
                          </div>
                          <div className="flex items-center justify-between rounded-md border px-3 py-2">
                            <div>
                              <p className="text-sm font-medium">Forkable</p>
                              <p className="text-[11px] text-muted-foreground">允许从 Exchange / 上游 release 派生</p>
                            </div>
                            <Switch
                              checked={Boolean(shareRecord.forkable)}
                              onCheckedChange={(checked) => updateWorkspaceManifest((current) => ({
                                ...current,
                                share: {
                                  ...asRecord(current.share),
                                  forkable: checked,
                                },
                              }))}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="structure" className="space-y-4">
                  {!workspaceManifestValid && (
                    <Card>
                      <CardContent className="p-4 text-sm text-amber-700">
                        Manifest JSON 当前无效。请先修复 JSON，再使用结构编辑。
                      </CardContent>
                    </Card>
                  )}
                  {workspaceManifestValid && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Capability & Agent</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="draft-capabilities">Capabilities</Label>
                            <Textarea
                              id="draft-capabilities"
                              rows={8}
                              className={advisorFieldClass('capabilities')}
                              value={capabilityLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => {
                                const nextLabels = parseLineList(event.target.value);
                                const existing = Array.isArray(current.capabilities) ? current.capabilities : [];
                                return {
                                  ...current,
                                  capabilities: nextLabels.map((label) => {
                                    const matched = existing.find((item) => {
                                      if (typeof item === 'string') return item === label;
                                      if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
                                      const record = item as Record<string, unknown>;
                                      return record.id === label || record.name === label;
                                    });
                                    if (matched && typeof matched === 'object' && !Array.isArray(matched)) {
                                      const record = matched as Record<string, unknown>;
                                      return {
                                        ...record,
                                        id: typeof record.id === 'string' ? record.id : slugifyToken(label),
                                        name: typeof record.name === 'string' ? record.name : label,
                                      };
                                    }
                                    return {
                                      id: slugifyToken(label),
                                      name: label,
                                      goal: '',
                                      inputs: [],
                                      outputs: [],
                                      dependencies: [],
                                      surfaceRefs: [],
                                      agentBindingRefs: [],
                                    };
                                  }),
                                };
                              })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="draft-agents">Agents</Label>
                            <Textarea
                              id="draft-agents"
                              rows={6}
                              className={advisorFieldClass('agents')}
                              value={agentLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => {
                                const nextLabels = parseLineList(event.target.value);
                                const existing = Array.isArray(current.agents) ? current.agents : [];
                                return {
                                  ...current,
                                  agents: nextLabels.map((label) => {
                                    const matched = existing.find((item) => {
                                      if (typeof item === 'string') return item === label;
                                      if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
                                      return (item as Record<string, unknown>).id === label;
                                    });
                                    if (matched && typeof matched === 'object' && !Array.isArray(matched)) {
                                      return matched;
                                    }
                                    return {
                                      id: label,
                                      mode: 'advisory_with_auto_actions',
                                      entryCapabilities: [],
                                    };
                                  }),
                                };
                              })}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Surface & Domain</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="draft-workspaces">Workspaces</Label>
                            <Textarea
                              id="draft-workspaces"
                              rows={6}
                              className={advisorFieldClass('surfaces.workspaces')}
                              value={workspaceLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                surfaces: {
                                  ...asRecord(current.surfaces),
                                  workspaces: parseLineList(event.target.value),
                                },
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="draft-entities">Domain Entities</Label>
                            <Textarea
                              id="draft-entities"
                              rows={6}
                              className={advisorFieldClass('domain.entities')}
                              value={entityLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                domain: {
                                  ...asRecord(current.domain),
                                  entities: parseLineList(event.target.value),
                                },
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="draft-taxonomy">Taxonomy Tags</Label>
                            <Textarea
                              id="draft-taxonomy"
                              rows={4}
                              value={taxonomyLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                domain: {
                                  ...asRecord(current.domain),
                                  taxonomyTags: parseLineList(event.target.value),
                                },
                              }))}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Outcome & Personas</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="draft-outcomes">Outcomes</Label>
                            <Textarea
                              id="draft-outcomes"
                              rows={5}
                              value={outcomeLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                intent: {
                                  ...asRecord(current.intent),
                                  outcomes: parseLineList(event.target.value),
                                },
                              }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="draft-personas">Personas</Label>
                            <Textarea
                              id="draft-personas"
                              rows={5}
                              value={personaLabels.join('\n')}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                intent: {
                                  ...asRecord(current.intent),
                                  personas: parseLineList(event.target.value),
                                },
                              }))}
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="graph" className="space-y-4">
                  {!workspaceManifestValid && (
                    <Card>
                      <CardContent className="p-4 text-sm text-amber-700">
                        Manifest JSON 当前无效。请先修复 JSON，再使用 graph 视图。
                      </CardContent>
                    </Card>
                  )}
                  {workspaceManifestValid && (
                    <>
                      <div className="grid gap-4 md:grid-cols-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Nodes</CardTitle>
                          </CardHeader>
                          <CardContent className="text-2xl font-semibold">{workspaceGraph.nodes.length}</CardContent>
                        </Card>
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Edges</CardTitle>
                          </CardHeader>
                          <CardContent className="text-2xl font-semibold">{workspaceGraph.edges.length}</CardContent>
                        </Card>
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Capabilities</CardTitle>
                          </CardHeader>
                          <CardContent className="text-2xl font-semibold">{capabilityLabels.length}</CardContent>
                        </Card>
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Workspaces</CardTitle>
                          </CardHeader>
                          <CardContent className="text-2xl font-semibold">{workspaceLabels.length}</CardContent>
                        </Card>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Navigation Bindings</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <Textarea
                              rows={10}
                              value={navigationBindingLines}
                              onChange={(event) => updateWorkspaceManifest((current) => ({
                                ...current,
                                surfaces: {
                                  ...asRecord(current.surfaces),
                                  navigationBindings: parseLineList(event.target.value).map((line) => {
                                    const [left, right] = line.split('->').map((part) => part.trim());
                                    const [entity, action] = (left ?? '').split(':').map((part) => part.trim());
                                    return {
                                      entity: entity || 'entity',
                                      action: action || 'open_workspace',
                                      targetSurface: right || 'surface',
                                    };
                                  }),
                                },
                              }))}
                              className="font-mono text-xs"
                            />
                            <p className="text-[11px] text-muted-foreground">
                              格式：<code>customer:open_workspace -&gt; foundation_customer_workspace</code>
                            </p>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Capability Dependencies</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <Textarea
                              rows={10}
                              value={capabilityDependencyLines}
                              onChange={(event) => updateWorkspaceManifest((current) => {
                                const existingCapabilities = Array.isArray(current.capabilities) ? current.capabilities : [];
                                const mapping = new Map(
                                  parseLineList(event.target.value).map((line) => {
                                    const [left, right] = line.split('->').map((part) => part.trim());
                                    return [left, parseLineList(right ?? '')] as const;
                                  }),
                                );
                                return {
                                  ...current,
                                  capabilities: existingCapabilities.map((capability) => {
                                    if (!capability || typeof capability !== 'object' || Array.isArray(capability)) {
                                      return capability;
                                    }
                                    const record = capability as Record<string, unknown>;
                                    const capabilityId = typeof record.id === 'string'
                                      ? record.id
                                      : (typeof record.name === 'string' ? slugifyToken(record.name) : '');
                                    if (!capabilityId || !mapping.has(capabilityId)) return capability;
                                    return {
                                      ...record,
                                      dependencies: mapping.get(capabilityId) ?? [],
                                    };
                                  }),
                                };
                              })}
                              className="font-mono text-xs"
                            />
                            <p className="text-[11px] text-muted-foreground">
                              格式：<code>dispatch_planning -&gt; customer_workspace, trip_plan</code>
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Node Map</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {workspaceGraph.nodes.length === 0 ? (
                              <p className="text-sm text-muted-foreground">暂无节点。</p>
                            ) : workspaceGraph.nodes.map((node) => (
                              <div key={node.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                                <span className="font-medium">{node.label}</span>
                                <Badge variant="outline">{node.kind}</Badge>
                              </div>
                            ))}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Edge Map</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {workspaceGraph.edges.length === 0 ? (
                              <p className="text-sm text-muted-foreground">暂无边关系。</p>
                            ) : workspaceGraph.edges.map((edge, index) => (
                              <div key={`${edge.from}-${edge.to}-${edge.label}-${index}`} className="rounded-md border px-3 py-2 font-mono text-xs text-muted-foreground">
                                {edge.from} -[{edge.label}]-&gt; {edge.to}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="manifest" className="space-y-2">
                  <Label htmlFor="draft-workspace-manifest">Manifest JSON</Label>
                  <Textarea
                    id="draft-workspace-manifest"
                    value={workspaceManifestText}
                    onChange={(event) => setWorkspaceManifestText(event.target.value)}
                    rows={22}
                    className="font-mono text-xs"
                  />
                </TabsContent>

                <TabsContent value="interview" className="space-y-2">
                  {!workspaceInterviewValid && (
                    <Card>
                      <CardContent className="p-4 text-sm text-amber-700">
                        Interview JSON 当前无效。你仍然可以直接修复原始 JSON。
                      </CardContent>
                    </Card>
                  )}
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Interview Summary</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="draft-interview-problem">Problem Statement</Label>
                          <Textarea
                            id="draft-interview-problem"
                            rows={4}
                            value={typeof workspaceInterviewState.problemStatement === 'string' ? workspaceInterviewState.problemStatement : ''}
                            onChange={(event) => updateWorkspaceInterview((current) => ({
                              ...current,
                              problemStatement: event.target.value,
                            }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="draft-interview-users">Target Users</Label>
                          <Textarea
                            id="draft-interview-users"
                            rows={4}
                            value={extractStringArray(workspaceInterviewState.targetUsers).join('\n')}
                            onChange={(event) => updateWorkspaceInterview((current) => ({
                              ...current,
                              targetUsers: parseLineList(event.target.value),
                            }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="draft-interview-constraints">Constraints</Label>
                          <Textarea
                            id="draft-interview-constraints"
                            rows={4}
                            value={extractStringArray(workspaceInterviewState.constraints).join('\n')}
                            onChange={(event) => updateWorkspaceInterview((current) => ({
                              ...current,
                              constraints: parseLineList(event.target.value),
                            }))}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Interview State JSON</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          id="draft-workspace-interview"
                          value={workspaceInterviewText}
                          onChange={(event) => setWorkspaceInterviewText(event.target.value)}
                          rows={22}
                          className="font-mono text-xs"
                        />
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="discussion" className="space-y-3">
                  {workspaceDraftId && (
                    <EntityDiscussionPanel
                      entityType="business_package_draft"
                      entityId={workspaceDraftId}
                      entityLabel="Business Package Draft"
                      entityTitle={workspaceDraft.draft.name ?? workspaceDraft.draft.slug}
                      active={workspaceTab === 'discussion'}
                    />
                  )}
                </TabsContent>

                <TabsContent value="ops" className="space-y-3 text-sm">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Latest Checks</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-xs text-muted-foreground">
                      <div>
                        preflight: {workspaceDraft.latestPreflight?.decision ?? 'not run'}
                        {workspaceDraft.latestPreflight?.result?.recommendedNextAction
                          ? ` -> ${workspaceDraft.latestPreflight.result.recommendedNextAction}`
                          : ''}
                      </div>
                      <div>
                        simulation: {workspaceDraft.latestSimulation?.result?.summary ?? 'not run'}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Release Bridge</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-xs text-muted-foreground">
                      {workspaceDraft.derivedRelease ? (
                        <div className="font-mono">
                          {workspaceDraft.derivedRelease.packageId}@{workspaceDraft.derivedRelease.version}
                        </div>
                      ) : (
                        <div>no derived release yet</div>
                      )}
                      {workspaceDraft.recentInstalls && workspaceDraft.recentInstalls.length > 0 && (
                        <div>
                          installs: {workspaceDraft.recentInstalls.map((install) => `${install.environment}/${install.runtimeStatus}`).join(' · ')}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
